<template>
    <div class="page-container">
        <div class="settings-form">
            <div class="header-section">
                <h2 class="page-title">名片管理</h2>

                <div class="stats-info">
                    <div class="stat-item">
                        <span class="label">总名片数：</span>
                        <span class="value">{{ stats.total }}</span>
                    </div>
                    <div class="stat-item">
                        <span class="label">公开名片：</span>
                        <span class="value">{{ stats.public }}</span>
                    </div>
                    <div class="stat-item">
                        <span class="label">主名片：</span>
                        <span class="value primary">{{ primaryCardName }}</span>
                    </div>
                </div>
            </div>


            <!-- 搜索和操作区域 -->
            <div class="search-bar">
                <el-input v-model="searchForm.name" placeholder="请输入姓名搜索" clearable size="large"
                    style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>

                <el-button type="primary" size="large" @click="handleSearch">
                    <el-icon>
                        <Search />
                    </el-icon>
                    搜索
                </el-button>

                <el-button type="success" size="large" @click="openAddDialog" style="float: right">
                    <el-icon>
                        <Plus />
                    </el-icon>
                    新增名片
                </el-button>

                <el-button type="info" size="large" @click="fetchCardList" :loading="loading" style="margin-left: 15px">
                    <el-icon>
                        <Refresh />
                    </el-icon>
                    刷新
                </el-button>
            </div>

            <!-- 名片列表 -->
            <el-table :data="cardList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
                <el-table-column label="主名片" width="80" align="center">
                    <template #default="{ row }">
                        <el-tooltip :content="row.is_primary ? '主名片' : '设为首页'" placement="top">
                            <el-icon :size="20" :color="row.is_primary ? '#67C23A' : '#909399'" class="primary-icon"
                                @click="setAsPrimary(row)" style="cursor: pointer;">
                                <StarFilled v-if="row.is_primary" />
                                <Star v-else />
                            </el-icon>
                        </el-tooltip>
                    </template>
                </el-table-column>

                <el-table-column label="头像" width="80" align="center">
                    <template #default="{ row }">
                        <el-avatar :size="40" :src="row.avatar || row.user_avatar" :alt="row.name">
                            {{ row.name.charAt(0) }}
                        </el-avatar>
                    </template>
                </el-table-column>

                <el-table-column prop="name" label="姓名" width="120" />
                <el-table-column label="关联用户" width="150">
                    <template #default="{ row }">
                        <div class="user-info">
                            <div class="user-nickname">{{ row.user_nickname || '未设置' }}</div>
                            <div class="user-phone">{{ row.user_phone }}</div>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="TX广告" width="180">
                    <template #default="{ row }">
                        <div v-if="row.gzh_name" class="gzh-info">
                            <div class="gzh-name">{{ row.gzh_name }}</div>
                            <div class="gzh-appid">{{ row.gzh_appid }}</div>
                        </div>
                        <span v-else class="empty-value">未关联</span>
                    </template>
                </el-table-column>
                <el-table-column prop="title" label="职位" width="150">
                    <template #default="{ row }">
                        <span v-if="row.title">{{ row.title }}</span>
                        <span v-else class="empty-value">未设置</span>
                    </template>
                </el-table-column>
                <el-table-column prop="company" label="公司" width="180">
                    <template #default="{ row }">
                        <span v-if="row.company">{{ row.company }}</span>
                        <span v-else class="empty-value">未设置</span>
                    </template>
                </el-table-column>

                <!-- 在名片列表的 el-table-column label="专属链接" 中修改 -->
                <el-table-column label="专属链接" width="350">
                    <template #default="{ row }">
                        <div class="card-url-section">
                            <el-popover placement="bottom" title="名片访问链接" :width="450" trigger="click" v-if="row.id">
                                <template #reference>
                                    <el-button type="primary" link style="padding: 8px 12px;">查看链接</el-button>
                                </template>
                                <div class="url-list">
                                    <div class="url-item">
                                        <span class="url-label">公众号链接：</span>
                                        <div class="url-content">
                                            <span class="url-text">{{ getOffiaccountUrl(row.id) }}</span>
                                            <el-button type="text" size="small"
                                                @click="copyUrl(getOffiaccountUrl(row.id))">
                                                复制
                                            </el-button>
                                        </div>
                                    </div>
                                    <div class="url-item">
                                        <span class="url-label">H5链接：</span>
                                        <div class="url-content">
                                            <span class="url-text">{{ getH5Url(row.id) }}</span>
                                            <el-button type="text" size="small" @click="copyUrl(getH5Url(row.id))">
                                                复制
                                            </el-button>
                                        </div>
                                    </div>
                                    <div class="url-item">
                                        <span class="url-label">小程序路径：</span>
                                        <div class="url-content">
                                            <div
                                                style="display: flex; justify-content: space-between; align-items: center;">
                                                <span class="url-text">{{ getMiniUrl(row.id) }}</span>
                                                <div class="mini-qrcode-actions">
                                                    <el-button type="text" size="small"
                                                        @click="copyUrl(getMiniUrl(row.id))">
                                                        复制
                                                    </el-button>
                                                    <el-button type="text" size="small"
                                                        @click.stop="generateMiniprogramQrcode(row)"
                                                        :loading="row.qrcode_loading" v-if="hasMiniprogramConfig">
                                                        <el-icon>
                                                            <Picture />
                                                        </el-icon>
                                                        {{ row.miniprogram_qrcode || row.miniprogram_qrcode_filename ?
                                                            '更新' : '生成' }}
                                                    </el-button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- 显示小程序二维码（如果已生成） -->
                                    <div class="qrcode-display" v-if="row.miniprogram_qrcode && !row.qrcode_loading">
                                        <div class="qrcode-title">
                                            <el-icon>
                                                <Picture />
                                            </el-icon>
                                            <span>小程序二维码</span>
                                            <el-tag size="small" type="success" style="margin-left: 8px">永久有效</el-tag>
                                        </div>
                                        <div class="qrcode-content">
                                            <img :src="row.miniprogram_qrcode" alt="小程序二维码" class="qrcode-image"
                                                @click="previewQrcode(row.miniprogram_qrcode, row.name)" />
                                            <div class="qrcode-actions">
                                                <el-button type="text" size="small"
                                                    @click="downloadQrcode(row.miniprogram_qrcode, row.name)">
                                                    <el-icon>
                                                        <Download />
                                                    </el-icon>
                                                    下载
                                                </el-button>
                                                <el-button type="text" size="small"
                                                    @click="copyUrl(row.miniprogram_qrcode)">
                                                    <el-icon>
                                                        <DocumentCopy />
                                                    </el-icon>
                                                    复制链接
                                                </el-button>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- 小程序码生成状态提示 -->
                                    <div class="qrcode-tip" v-if="row.qrcode_loading">
                                        <el-icon class="is-loading">
                                            <Loading />
                                        </el-icon>
                                        生成小程序码中...
                                    </div>
                                </div>
                            </el-popover>
                            <span v-else class="empty-value">未生成</span>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="状态" width="100">
                    <template #default="{ row }">
                        <el-tag :type="row.is_public ? 'success' : 'info'" size="small">
                            {{ row.is_public ? '公开' : '私密' }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column prop="create_time" label="创建时间" width="180" />
                <el-table-column prop="update_time" label="更新时间" width="180" />

                <el-table-column label="操作" width="480" fixed="right">
                    <template #default="scope">
                        <el-button type="primary" size="small" @click="openEditDialog(scope.row)"><el-icon>
                                <Edit />
                            </el-icon>编辑</el-button>
                        <el-button type="warning" size="small" @click="openProductDialog(scope.row)"><el-icon>
                                <Goods />
                            </el-icon>产品</el-button>
                        <el-button type="info" size="small" @click="openAdDialog(scope.row)"><el-icon>
                                <Promotion />
                            </el-icon>广告</el-button>
                        <!-- 新增：自定义按钮 -->
                        <el-button type="success" size="small" @click="openCustomDialog(scope.row)"><el-icon>
                                <Setting />
                            </el-icon>自定义</el-button>
                        <el-button type="danger" size="small" @click="handleDelete(scope.row.id)"
                            :disabled="scope.row.is_primary"><el-icon>
                                <Delete />
                            </el-icon>删除</el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                    :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                    layout="total, sizes, prev, pager, next, jumper" @size-change="fetchCardList"
                    @current-change="fetchCardList" />
            </div>
        </div>

        <!-- ==================== 自定义管理对话框 ==================== -->
        <el-dialog v-model="customDialogVisible" :title="`自定义管理 - ${currentCard?.name}`" width="1100px" top="3vh"
            :close-on-click-modal="false">
            <el-tabs v-model="customActiveTab" type="border-card">

                <!-- ========== 首页顺序 Tab ========== -->
                <el-tab-pane label="首页顺序" name="sections">
                    <div class="custom-tab-content">
                        <el-alert type="info" show-icon :closable="false" style="margin-bottom: 16px;">
                            <template #title>调整板块排序，修改名称后点击"保存"按钮可单独保存每个板块</template>
                        </el-alert>
                        <el-table :data="sectionList" stripe row-key="section_key">
                            <el-table-column label="排序" width="80">
                                <template #default="{ row }">
                                    <el-input-number v-model="row.sort_order" :min="0" :max="99" size="small"
                                        controls-position="right" style="width: 80px" />
                                </template>
                            </el-table-column>
                            <el-table-column prop="section_key" label="板块标识" width="120" />
                            <el-table-column label="原名称" width="120">
                                <template #default="{ row }">
                                    <el-tag type="info" size="small">{{ getDefaultSectionName(row.section_key)
                                        }}</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="自定义名称" width="180">
                                <template #default="{ row }">
                                    <el-input v-model="row.section_name" placeholder="留空使用原名称" size="small" />
                                </template>
                            </el-table-column>
                            <el-table-column label="显示名称" width="150">
                                <template #default="{ row }">
                                    <span style="font-weight: 500;">{{ row.section_name ||
                                        getDefaultSectionName(row.section_key) }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="操作">
                                <template #default="{ row }">
                                    <el-button type="primary" size="small" @click="saveSectionName(row)">保存</el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                        <div style="margin-top: 16px; text-align: center;">
                            <el-button type="primary" @click="saveAllSections"
                                :loading="sectionSaving">保存全部排序</el-button>
                        </div>
                    </div>
                </el-tab-pane>

                <!-- ========== 权限控制 Tab ========== -->
                <el-tab-pane label="权限控制" name="permissions">
                    <div class="custom-tab-content">
                        <el-table :data="sectionList" stripe>
                            <el-table-column label="板块名称" width="200">
                                <template #default="{ row }">
                                    {{ row.section_name || getDefaultSectionName(row.section_key) }}
                                </template>
                            </el-table-column>
                            <el-table-column label="板块标识" width="120">
                                <template #default="{ row }">
                                    <el-tag type="info" size="small">{{ row.section_key }}</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="显示开关" width="100" align="center">
                                <template #default="{ row }">
                                    <el-switch v-model="row.is_visible" :active-value="1" :inactive-value="0"
                                        @change="saveSectionVisibility(row)" />
                                </template>
                            </el-table-column>
                            <el-table-column label="状态">
                                <template #default="{ row }">
                                    <el-tag :type="row.is_visible ? 'success' : 'danger'" size="small">
                                        {{ row.is_visible ? '显示' : '隐藏' }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </el-tab-pane>

                <!-- ========== 首页管理 Tab ========== -->
                <el-tab-pane label="首页管理" name="homepage">
                    <el-tabs v-model="homepageSubTab" type="card" class="homepage-subtabs">

                        <!-- 个人简介 -->
                        <el-tab-pane label="个人简介" name="profile">
                            <div class="custom-tab-content">
                                <el-form label-width="100px">
                                    <el-form-item label="个人简介">
                                        <el-input v-model="profileContent" type="textarea" :rows="6"
                                            placeholder="请输入个人简介内容" />
                                    </el-form-item>
                                    <el-form-item>
                                        <el-button type="primary" @click="saveProfile"
                                            :loading="profileSaving">保存简介</el-button>
                                    </el-form-item>
                                </el-form>
                            </div>
                        </el-tab-pane>

                        <!-- 个性标签 -->
                        <el-tab-pane label="个性标签" name="tags">
                            <div class="custom-tab-content">
                                <div style="margin-bottom: 16px;">
                                    <el-button type="primary" @click="openAddTagDialog"><el-icon>
                                            <Plus />
                                        </el-icon>添加标签</el-button>
                                </div>
                                <el-table :data="tagList" stripe v-loading="tagLoading">
                                    <el-table-column prop="tag_name" label="标签名称" width="200" />
                                    <el-table-column label="虚拟点赞" width="150">
                                        <template #default="{ row }">
                                            <el-input-number v-model="row.virtual_likes" :min="0" size="small"
                                                controls-position="right" />
                                        </template>
                                    </el-table-column>
                                    <el-table-column label="真实点赞" width="100">
                                        <template #default="{ row }">
                                            <el-tag type="success" size="small">{{ row.real_likes || 0 }}</el-tag>
                                        </template>
                                    </el-table-column>
                                    <el-table-column prop="sort_order" label="排序" width="100" />
                                    <el-table-column label="操作" show-overflow-tooltip>
                                        <template #default="{ row }">
                                            <el-button type="primary" size="small" @click="saveTag(row)">保存</el-button>
                                            <el-button type="danger" size="small"
                                                @click="deleteTag(row.id)">删除</el-button>
                                        </template>
                                    </el-table-column>
                                </el-table>
                            </div>
                        </el-tab-pane>

                        <!-- 我的企业 -->
                        <el-tab-pane label="我的企业" name="companies">
                            <div class="custom-tab-content">
                                <div style="margin-bottom: 16px;">
                                    <el-button type="primary" @click="openAddCompanyDialog"><el-icon>
                                            <Plus />
                                        </el-icon>添加企业</el-button>
                                </div>
                                <el-table :data="companyList" stripe v-loading="companyLoading">
                                    <el-table-column label="Logo" width="80">
                                        <template #default="{ row }">
                                            <el-avatar :size="40" :src="row.logo" shape="square" />
                                        </template>
                                    </el-table-column>
                                    <el-table-column prop="company_name" label="公司名称" width="180" />
                                    <el-table-column prop="industry" label="行业" width="120" />
                                    <el-table-column prop="website" label="网址" width="200" show-overflow-tooltip />
                                    <el-table-column prop="sort_order" label="排序" width="80" />
                                    <el-table-column label="操作" show-overflow-tooltip>
                                        <template #default="{ row }">
                                            <el-button type="primary" size="small"
                                                @click="openEditCompanyDialog(row)">编辑</el-button>
                                            <el-button type="danger" size="small"
                                                @click="deleteCompany(row.id)">删除</el-button>
                                        </template>
                                    </el-table-column>
                                </el-table>
                            </div>
                        </el-tab-pane>

                        <!-- 视频展示 -->
                        <el-tab-pane label="视频展示" name="videos">
                            <div class="custom-tab-content">
                                <div style="margin-bottom: 16px;">
                                    <el-button type="primary" @click="openAddVideoDialog"><el-icon>
                                            <Plus />
                                        </el-icon>添加视频</el-button>
                                </div>
                                <el-table :data="videoList" stripe v-loading="videoLoading">
                                    <el-table-column label="封面" width="100">
                                        <template #default="{ row }">
                                            <el-image v-if="row.video_cover" :src="row.video_cover"
                                                style="width:60px;height:60px;border-radius:4px;" fit="cover" />
                                            <span v-else class="empty-value">无封面</span>
                                        </template>
                                    </el-table-column>
                                    <el-table-column prop="video_name" label="名称" width="150" />
                                    <el-table-column label="虚拟/真实点赞" width="140">
                                        <template #default="{ row }">
                                            <div>虚拟：{{ row.virtual_likes || 0 }}</div>
                                            <div>真实：<el-tag type="success" size="small">{{ row.real_likes || 0
                                                    }}</el-tag></div>
                                        </template>
                                    </el-table-column>
                                    <el-table-column label="虚拟/真实评论" width="140">
                                        <template #default="{ row }">
                                            <div>虚拟：{{ row.virtual_comments || 0 }}</div>
                                            <div>真实：<el-tag type="warning" size="small">{{ row.real_comments || 0
                                                    }}</el-tag></div>
                                        </template>
                                    </el-table-column>
                                    <el-table-column label="虚拟/真实转发" width="140">
                                        <template #default="{ row }">
                                            <div>虚拟：{{ row.virtual_shares || 0 }}</div>
                                            <div>真实：<el-tag type="info" size="small">{{ row.real_shares || 0 }}</el-tag>
                                            </div>
                                        </template>
                                    </el-table-column>
                                    <el-table-column prop="sort_order" label="排序" width="70" />
                                    <el-table-column label="操作" show-overflow-tooltip>
                                        <template #default="{ row }">
                                            <el-button type="primary" size="small"
                                                @click="openEditVideoDialog(row)">编辑</el-button>
                                            <el-button type="danger" size="small"
                                                @click="deleteVideo(row.id)">删除</el-button>
                                        </template>
                                    </el-table-column>
                                </el-table>
                            </div>
                        </el-tab-pane>

                        <!-- 图片展示 -->
                        <el-tab-pane label="图片展示" name="images">
                            <div class="custom-tab-content">
                                <div style="margin-bottom: 16px;">
                                    <el-button type="primary" @click="openAddImageDialog"><el-icon>
                                            <Plus />
                                        </el-icon>添加图片</el-button>
                                </div>
                                <el-table :data="imageList" stripe v-loading="imageLoading">
                                    <el-table-column label="图片" width="100">
                                        <template #default="{ row }">
                                            <el-image :src="row.image_url"
                                                style="width:60px;height:60px;border-radius:4px;" fit="cover" />
                                        </template>
                                    </el-table-column>
                                    <el-table-column prop="image_name" label="名称" width="150" />
                                    <el-table-column prop="image_desc" label="描述" show-overflow-tooltip />
                                    <el-table-column prop="sort_order" label="排序" width="80" />
                                    <el-table-column label="操作" width="150">
                                        <template #default="{ row }">
                                            <el-button type="primary" size="small"
                                                @click="openEditImageDialog(row)">编辑</el-button>
                                            <el-button type="danger" size="small"
                                                @click="deleteImage(row.id)">删除</el-button>
                                        </template>
                                    </el-table-column>
                                </el-table>
                            </div>
                        </el-tab-pane>

                        <!-- 音频展示 -->
                        <el-tab-pane label="音频展示" name="audios">
                            <div class="custom-tab-content">
                                <div style="margin-bottom: 16px;">
                                    <el-button type="primary" @click="openAddAudioDialog"><el-icon>
                                            <Plus />
                                        </el-icon>添加音频</el-button>
                                </div>
                                <el-table :data="audioList" stripe v-loading="audioLoading">
                                    <el-table-column prop="audio_name" label="名称" width="200" />
                                    <el-table-column prop="audio_desc" label="描述" show-overflow-tooltip />
                                    <el-table-column prop="sort_order" label="排序" width="80" />
                                    <el-table-column label="操作" width="150">
                                        <template #default="{ row }">
                                            <el-button type="primary" size="small"
                                                @click="openEditAudioDialog(row)">编辑</el-button>
                                            <el-button type="danger" size="small"
                                                @click="deleteAudio(row.id)">删除</el-button>
                                        </template>
                                    </el-table-column>
                                </el-table>
                            </div>
                        </el-tab-pane>
                    </el-tabs>
                </el-tab-pane>

                <!-- ========== 在线客服 Tab ========== -->
                <el-tab-pane label="在线客服" name="customer_service">
                    <div class="custom-tab-content">
                        <div style="margin-bottom: 16px;">
                            <el-button type="primary" @click="openAddCustomerServiceDialog"><el-icon>
                                    <Plus />
                                </el-icon>添加客服</el-button>
                        </div>
                        <el-table :data="customerServiceList" stripe v-loading="csLoading">
                            <el-table-column label="类型" width="120">
                                <template #default="{ row }">
                                    <el-tag :type="getServiceTypeTag(row.service_type)" size="small">{{
                                        getServiceTypeText(row.service_type) }}</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column prop="service_name" label="名称" width="150" />
                            <el-table-column prop="service_account" label="账号" width="150" show-overflow-tooltip />
                            <el-table-column label="二维码" width="80">
                                <template #default="{ row }">
                                    <el-image v-if="row.qr_code" :src="row.qr_code" style="width:40px;height:40px;"
                                        fit="cover" :preview-src-list="[row.qr_code]" />
                                    <span v-else class="empty-value">-</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="description" label="描述" show-overflow-tooltip />
                            <el-table-column prop="service_time" label="服务时间" width="120" />
                            <el-table-column label="状态" width="80">
                                <template #default="{ row }">
                                    <el-tag :type="row.is_active ? 'success' : 'info'" size="small">{{ row.is_active ?
                                        '激活' : '禁用' }}</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column prop="sort_order" label="排序" width="70" />
                            <el-table-column label="操作" width="150">
                                <template #default="{ row }">
                                    <el-button type="primary" size="small"
                                        @click="openEditCustomerServiceDialog(row)">编辑</el-button>
                                    <el-button type="danger" size="small"
                                        @click="deleteCustomerService(row.id)">删除</el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </el-tab-pane>

                <!-- ========== 意见反馈 Tab ========== -->
                <el-tab-pane label="意见反馈" name="feedback">
                    <div class="custom-tab-content">
                        <el-table :data="feedbackList" stripe v-loading="feedbackLoading">
                            <el-table-column label="用户" width="120">
                                <template #default="{ row }">
                                    <div>{{ row.user_nickname || '匿名' }}</div>
                                    <div style="font-size:12px;color:#909399;">{{ row.user_phone || '' }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="类型" width="100">
                                <template #default="{ row }">
                                    <el-tag :type="getFeedbackTypeTag(row.feedback_type)" size="small">{{
                                        getFeedbackTypeText(row.feedback_type) }}</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column prop="title" label="标题" width="180" show-overflow-tooltip />
                            <el-table-column prop="content" label="内容" show-overflow-tooltip />
                            <el-table-column label="图片" width="80">
                                <template #default="{ row }">
                                    <el-popover v-if="row.images && row.images.length > 0" placement="left" :width="300"
                                        trigger="click">
                                        <template #reference>
                                            <el-button type="primary" link size="small">{{ row.images.length
                                                }}张</el-button>
                                        </template>
                                        <div class="feedback-images">
                                            <el-image v-for="(img, idx) in row.images" :key="idx" :src="img"
                                                style="width:80px;height:80px;margin:4px;border-radius:4px;" fit="cover"
                                                :preview-src-list="row.images" />
                                        </div>
                                    </el-popover>
                                    <span v-else class="empty-value">无</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="状态" width="100">
                                <template #default="{ row }">
                                    <el-tag :type="getFeedbackStatusTag(row.status)" size="small">{{
                                        getFeedbackStatusText(row.status) }}</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="回复内容" width="200" show-overflow-tooltip>
                                <template #default="{ row }">
                                    <span v-if="row.reply_content">{{ row.reply_content }}</span>
                                    <span v-else class="empty-value">未回复</span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="create_time" label="提交时间" width="160" />
                            <el-table-column label="操作" width="120" fixed="right">
                                <template #default="{ row }">
                                    <el-button type="primary" size="small"
                                        @click="openReplyFeedbackDialog(row)">回复</el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                        <div class="pagination-container" v-if="feedbackPagination.total > 0">
                            <el-pagination v-model:current-page="feedbackPagination.page"
                                v-model:page-size="feedbackPagination.pageSize" :total="feedbackPagination.total"
                                :page-sizes="[5, 10, 20]" layout="total, sizes, prev, pager, next, jumper"
                                @size-change="fetchFeedbackList" @current-change="fetchFeedbackList" />
                        </div>
                    </div>
                </el-tab-pane>

                <!-- ========== 关于我们 Tab ========== -->
                <el-tab-pane label="关于我们" name="about_us">
                    <div class="custom-tab-content">
                        <el-form ref="aboutUsForm" :model="aboutUsForm" label-width="120px">
                            <el-row :gutter="20">
                                <el-col :span="12">
                                    <el-form-item label="标题">
                                        <el-input v-model="aboutUsForm.title" placeholder="请输入标题" />
                                    </el-form-item>
                                    <el-form-item label="公司名称">
                                        <el-input v-model="aboutUsForm.company_name" placeholder="请输入公司名称" />
                                    </el-form-item>
                                    <el-form-item label="公司地址">
                                        <el-input v-model="aboutUsForm.company_address" placeholder="请输入公司地址" />
                                    </el-form-item>
                                    <el-form-item label="公司电话">
                                        <el-input v-model="aboutUsForm.company_phone" placeholder="请输入公司电话" />
                                    </el-form-item>
                                </el-col>
                                <el-col :span="12">
                                    <el-form-item label="公司邮箱">
                                        <el-input v-model="aboutUsForm.company_email" placeholder="请输入公司邮箱" />
                                    </el-form-item>
                                    <el-form-item label="官方网站">
                                        <el-input v-model="aboutUsForm.official_website" placeholder="请输入官方网站" />
                                    </el-form-item>
                                    <el-form-item label="公司Logo">
                                        <div class="upload-section">
                                            <div v-if="aboutUsForm.logo" class="logo-preview">
                                                <el-image :src="aboutUsForm.logo"
                                                    style="width:80px;height:80px;border-radius:8px;" fit="cover" />
                                                <el-button type="danger" size="small"
                                                    @click="aboutUsForm.logo = ''">删除</el-button>
                                            </div>
                                            <el-upload :auto-upload="false" :show-file-list="false"
                                                :on-change="handleAboutUsLogoChange" accept="image/*"
                                                :disabled="aboutUsLogoUploading">
                                                <el-button type="primary" :loading="aboutUsLogoUploading"><el-icon>
                                                        <Upload />
                                                    </el-icon>{{ aboutUsForm.logo ? '更换' : '上传' }}</el-button>
                                            </el-upload>
                                        </div>
                                    </el-form-item>
                                </el-col>
                            </el-row>

                            <!-- 修改为 -->
                            <el-form-item label="内容（富文本）">
                                <div class="quill-editor-wrapper">
                                    <QuillEditor ref="aboutUsQuillEditor" v-model:content="aboutUsForm.content"
                                        contentType="html" theme="snow" :toolbar="aboutUsToolbar"
                                        @ready="onAboutUsQuillReady" />
                                </div>
                            </el-form-item>

                            <!-- 社交媒体链接 -->
                            <el-form-item label="社交媒体链接">
                                <!-- 按钮放在上方，独占一行 -->
                                <div style="margin-bottom: 12px; text-align: left; width: 100%; display: block;">
                                    <el-button type="primary" size="small" @click="addSocialLink">
                                        <el-icon>
                                            <Plus />
                                        </el-icon>添加链接
                                    </el-button>
                                </div>
                                <!-- 数据列表在下方 -->
                                <div class="social-links-scroll">
                                    <div v-for="(link, index) in aboutUsForm.social_links" :key="index"
                                        class="social-link-item">
                                        <!-- 保持原有的横向布局 -->
                                        <el-input v-model="link.icon" placeholder="图标URL"
                                            style="width:150px;margin-right:8px;" size="small" />
                                        <el-input v-model="link.name" placeholder="名称"
                                            style="width:120px;margin-right:8px;" size="small" />
                                        <el-input v-model="link.url" placeholder="地址"
                                            style="width:200px;margin-right:8px;" size="small" />
                                        <el-upload :auto-upload="false" :show-file-list="false"
                                            :on-change="(file) => uploadSocialIcon(file, index)" accept="image/*"
                                            style="display:inline-block;margin-right:8px;">
                                            <el-button size="small"><el-icon>
                                                    <Upload />
                                                </el-icon></el-button>
                                        </el-upload>
                                        <el-button type="danger" size="small" @click="removeSocialLink(index)">
                                            <el-icon>
                                                <Delete />
                                            </el-icon>
                                        </el-button>
                                    </div>
                                    <div v-if="aboutUsForm.social_links.length === 0" class="empty-links">
                                        暂无社交媒体链接，点击上方按钮添加
                                    </div>
                                </div>
                            </el-form-item>


                            <!-- 相关图片 -->
                            <el-form-item label="相关图片">
                                <!-- 按钮放在上方，独占一行 -->
                                <div style="margin-bottom: 12px; width: 100%; display: block;">
                                    <el-upload :auto-upload="false" :show-file-list="false"
                                        :on-change="handleAboutUsImageChange" accept="image/*" multiple
                                        :disabled="aboutUsImageUploading">
                                        <el-button type="primary" :loading="aboutUsImageUploading">
                                            <el-icon>
                                                <Upload />
                                            </el-icon>上传图片
                                        </el-button>
                                    </el-upload>
                                </div>
                                <!-- 图片画廊在下方 -->
                                <div class="about-us-images">
                                    <div v-for="(img, index) in aboutUsForm.images" :key="index"
                                        class="about-us-image-item">
                                        <el-image :src="img" style="width:100px;height:100px;border-radius:4px;"
                                            fit="cover" :preview-src-list="[img]" />
                                        <el-button type="danger" size="small"
                                            @click="aboutUsForm.images.splice(index, 1)" style="margin-top:4px;">
                                            删除
                                        </el-button>
                                    </div>
                                    <div v-if="aboutUsForm.images.length === 0" class="empty-images">
                                        暂无相关图片，点击上方按钮上传
                                    </div>
                                </div>
                            </el-form-item>



                            <el-form-item>
                                <el-button type="primary" @click="saveAboutUs"
                                    :loading="aboutUsSaving">保存关于我们</el-button>
                            </el-form-item>
                        </el-form>
                    </div>
                </el-tab-pane>
            </el-tabs>
            <template #footer>
                <el-button @click="customDialogVisible = false">关闭</el-button>
            </template>
        </el-dialog>


        <!-- ==================== 添加标签对话框 ==================== -->
        <el-dialog v-model="tagDialogVisible" title="添加标签" width="400px">
            <el-form>
                <el-form-item label="标签名称" required>
                    <el-input v-model="tagForm.tag_name" placeholder="请输入标签名称" />
                </el-form-item>
                <el-form-item label="虚拟点赞数">
                    <el-input-number v-model="tagForm.virtual_likes" :min="0" />
                </el-form-item>
                <el-form-item label="排序">
                    <el-input-number v-model="tagForm.sort_order" :min="0" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="tagDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitTag" :loading="tagSubmitting">确认</el-button>
            </template>
        </el-dialog>

        <!-- ==================== 添加/编辑企业对话框 ==================== -->
        <el-dialog v-model="companyDialogVisible" :title="companyDialogTitle" width="500px">
            <el-form>
                <el-form-item label="企业Logo">
                    <div class="upload-section">
                        <div v-if="companyForm.logo" class="logo-preview">
                            <el-image :src="companyForm.logo" style="width:80px;height:80px;border-radius:8px;"
                                fit="cover" />
                            <el-button type="danger" size="small" @click="companyForm.logo = ''">删除</el-button>
                        </div>
                        <el-upload :auto-upload="false" :show-file-list="false" :on-change="handleCompanyLogoChange"
                            accept="image/*" :disabled="companyLogoUploading">
                            <el-button type="primary" :loading="companyLogoUploading"><el-icon>
                                    <Upload />
                                </el-icon>{{ companyForm.logo ? '更换' : '上传' }}</el-button>
                        </el-upload>
                    </div>
                </el-form-item>
                <el-form-item label="公司名称" required>
                    <el-input v-model="companyForm.company_name" placeholder="请输入公司名称" />
                </el-form-item>
                <el-form-item label="所属行业">
                    <el-input v-model="companyForm.industry" placeholder="请输入所属行业" />
                </el-form-item>
                <el-form-item label="企业网址">
                    <el-input v-model="companyForm.website" placeholder="请输入企业网址" />
                </el-form-item>
                <el-form-item label="企业描述">
                    <el-input v-model="companyForm.description" type="textarea" :rows="3" placeholder="请输入企业描述" />
                </el-form-item>
                <el-form-item label="排序">
                    <el-input-number v-model="companyForm.sort_order" :min="0" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="companyDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitCompany" :loading="companySubmitting">确认</el-button>
            </template>
        </el-dialog>

        <!-- ==================== 添加/编辑视频对话框 ==================== -->
        <el-dialog v-model="videoDialogVisible" :title="videoDialogTitle" width="600px">
            <el-form>
                <el-form-item label="视频名称" required>
                    <el-input v-model="videoForm.video_name" placeholder="请输入视频名称" />
                </el-form-item>
                <!-- 修改：视频URL改为支持上传 -->
                <el-form-item label="视频文件" required>
                    <div class="upload-section">
                        <div v-if="videoForm.video_url" class="video-preview">
                            <video :src="videoForm.video_url" style="width:200px;height:120px;border-radius:4px;"
                                controls />
                            <el-button type="danger" size="small" @click="videoForm.video_url = ''">删除</el-button>
                        </div>
                        <el-upload :auto-upload="false" :show-file-list="false" :on-change="handleVideoFileChange"
                            accept="video/*" :disabled="videoFileUploading">
                            <el-button type="primary" :loading="videoFileUploading">
                                <el-icon>
                                    <Upload />
                                </el-icon>
                                {{ videoForm.video_url ? '更换视频' : '上传视频' }}
                            </el-button>
                            <template #tip>
                                <div class="el-upload__tip">支持 MP4、MOV、AVI、WebM 格式，大小不超过 100MB</div>
                            </template>
                        </el-upload>
                    </div>
                </el-form-item>
                <el-form-item label="视频封面">
                    <div class="upload-section">
                        <div v-if="videoForm.video_cover" class="logo-preview">
                            <el-image :src="videoForm.video_cover" style="width:120px;height:68px;border-radius:4px;"
                                fit="cover" />
                            <el-button type="danger" size="small" @click="videoForm.video_cover = ''">删除</el-button>
                        </div>
                        <el-upload :auto-upload="false" :show-file-list="false" :on-change="handleVideoCoverChange"
                            accept="image/*" :disabled="videoCoverUploading">
                            <el-button type="primary" :loading="videoCoverUploading"><el-icon>
                                    <Upload />
                                </el-icon>{{ videoForm.video_cover ? '更换' : '上传封面' }}</el-button>
                        </el-upload>
                    </div>
                </el-form-item>
                <el-form-item label="视频描述">
                    <el-input v-model="videoForm.video_desc" type="textarea" :rows="3" placeholder="请输入视频描述" />
                </el-form-item>
                <el-row :gutter="20">
                    <el-col :span="8">
                        <el-form-item label="虚拟点赞">
                            <el-input-number v-model="videoForm.virtual_likes" :min="0" controls-position="right" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="虚拟评论">
                            <el-input-number v-model="videoForm.virtual_comments" :min="0" controls-position="right" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="虚拟转发">
                            <el-input-number v-model="videoForm.virtual_shares" :min="0" controls-position="right" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="排序">
                    <el-input-number v-model="videoForm.sort_order" :min="0" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="videoDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitVideo" :loading="videoSubmitting">确认</el-button>
            </template>
        </el-dialog>

        <!-- ==================== 添加/编辑图片对话框 ==================== -->
        <el-dialog v-model="imageDialogVisible" :title="imageDialogTitle" width="500px">
            <el-form>
                <el-form-item label="图片" required>
                    <div class="upload-section">
                        <div v-if="imageForm.image_url" class="logo-preview">
                            <el-image :src="imageForm.image_url" style="width:120px;height:120px;border-radius:4px;"
                                fit="cover" />
                            <el-button type="danger" size="small" @click="imageForm.image_url = ''">删除</el-button>
                        </div>
                        <el-upload :auto-upload="false" :show-file-list="false" :on-change="handleImageUploadChange"
                            accept="image/*" :disabled="imageUploadLoading">
                            <el-button type="primary" :loading="imageUploadLoading"><el-icon>
                                    <Upload />
                                </el-icon>{{ imageForm.image_url ? '更换' : '上传图片' }}</el-button>
                        </el-upload>
                    </div>
                </el-form-item>
                <el-form-item label="图片名称">
                    <el-input v-model="imageForm.image_name" placeholder="请输入图片名称" />
                </el-form-item>
                <el-form-item label="图片描述">
                    <el-input v-model="imageForm.image_desc" type="textarea" :rows="3" placeholder="请输入图片描述" />
                </el-form-item>
                <el-form-item label="排序">
                    <el-input-number v-model="imageForm.sort_order" :min="0" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="imageDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitImage" :loading="imageSubmitting">确认</el-button>
            </template>
        </el-dialog>

        <!-- ==================== 添加/编辑音频对话框 ==================== -->
        <el-dialog v-model="audioDialogVisible" :title="audioDialogTitle" width="500px">
            <el-form>
                <el-form-item label="音频名称" required>
                    <el-input v-model="audioForm.audio_name" placeholder="请输入音频名称" />
                </el-form-item>
                <!-- 修改：音频URL改为支持上传 -->
                <el-form-item label="音频文件" required>
                    <div class="upload-section">
                        <div v-if="audioForm.audio_url" class="audio-preview">
                            <audio :src="audioForm.audio_url" style="width:100%;" controls />
                            <el-button type="danger" size="small" @click="audioForm.audio_url = ''">删除</el-button>
                        </div>
                        <el-upload :auto-upload="false" :show-file-list="false" :on-change="handleAudioFileChange"
                            accept="audio/*" :disabled="audioFileUploading">
                            <el-button type="primary" :loading="audioFileUploading">
                                <el-icon>
                                    <Upload />
                                </el-icon>
                                {{ audioForm.audio_url ? '更换音频' : '上传音频' }}
                            </el-button>
                            <template #tip>
                                <div class="el-upload__tip">支持 MP3、WAV、OGG 等格式，大小不超过 20MB</div>
                            </template>
                        </el-upload>
                    </div>
                </el-form-item>
                <el-form-item label="音频描述">
                    <el-input v-model="audioForm.audio_desc" type="textarea" :rows="3" placeholder="请输入音频描述" />
                </el-form-item>
                <el-form-item label="排序">
                    <el-input-number v-model="audioForm.sort_order" :min="0" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="audioDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitAudio" :loading="audioSubmitting">确认</el-button>
            </template>
        </el-dialog>

        <!-- ==================== 添加/编辑客服对话框 ==================== -->
        <el-dialog v-model="csDialogVisible" :title="csDialogTitle" width="550px">
            <el-form>
                <el-form-item label="客服类型" required>
                    <el-select v-model="csForm.service_type" placeholder="请选择客服类型" style="width:100%;"
                        @change="handleCsTypeChange">
                        <el-option label="微信客服" value="wechat" />
                        <el-option label="电话客服" value="phone" />
                        <el-option label="QQ客服" value="qq" />
                        <el-option label="在线客服" value="online" />
                        <el-option label="小程序客服" value="miniprogram" />
                    </el-select>
                </el-form-item>
                <el-form-item label="客服名称" required>
                    <el-input v-model="csForm.service_name" placeholder="请输入客服名称" />
                </el-form-item>
                <el-form-item v-if="['wechat', 'qq'].includes(csForm.service_type)" label="客服账号" required>
                    <el-input v-model="csForm.service_account" placeholder="请输入客服账号/微信号" />
                </el-form-item>
                <el-form-item v-if="csForm.service_type === 'phone'" label="电话号码" required>
                    <el-input v-model="csForm.service_account" placeholder="请输入电话号码" />
                </el-form-item>
                <el-form-item v-if="['wechat', 'qq'].includes(csForm.service_type)" label="客服二维码">
                    <div class="upload-section">
                        <div v-if="csForm.qr_code" class="logo-preview">
                            <el-image :src="csForm.qr_code" style="width:80px;height:80px;border-radius:4px;"
                                fit="cover" />
                            <el-button type="danger" size="small" @click="csForm.qr_code = ''">删除</el-button>
                        </div>
                        <el-upload :auto-upload="false" :show-file-list="false" :on-change="handleCsQrcodeChange"
                            accept="image/*" :disabled="csQrcodeUploading">
                            <el-button type="primary" :loading="csQrcodeUploading"><el-icon>
                                    <Upload />
                                </el-icon>{{ csForm.qr_code ? '更换' : '上传二维码' }}</el-button>
                        </el-upload>
                    </div>
                </el-form-item>
                <el-form-item v-if="['online', 'miniprogram'].includes(csForm.service_type)" label="客服链接">
                    <el-input v-model="csForm.service_url" placeholder="请输入客服链接" />
                </el-form-item>
                <el-form-item label="客服描述" required>
                    <el-input v-model="csForm.description" type="textarea" :rows="2" placeholder="请输入客服描述" />
                </el-form-item>
                <el-form-item label="服务时间" required>
                    <el-input v-model="csForm.service_time" placeholder="如：周一至周五 9:00-18:00" />
                </el-form-item>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="排序">
                            <el-input-number v-model="csForm.sort_order" :min="0" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="激活状态">
                            <el-switch v-model="csForm.is_active" :active-value="1" :inactive-value="0" />
                        </el-form-item>
                    </el-col>
                </el-row>
            </el-form>
            <template #footer>
                <el-button @click="csDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitCustomerService" :loading="csSubmitting">确认</el-button>
            </template>
        </el-dialog>

        <!-- ==================== 回复反馈对话框 ==================== -->
        <el-dialog v-model="replyDialogVisible" title="回复反馈" width="500px">
            <el-form>
                <el-form-item label="反馈用户">
                    <el-input :value="replyForm.user_nickname || '匿名用户'" disabled />
                </el-form-item>
                <el-form-item label="反馈标题">
                    <el-input :value="replyForm.title" disabled />
                </el-form-item>
                <el-form-item label="反馈内容">
                    <el-input :value="replyForm.content" type="textarea" :rows="3" disabled />
                </el-form-item>
                <el-form-item label="回复内容" required>
                    <el-input v-model="replyForm.reply_content" type="textarea" :rows="4" placeholder="请输入回复内容" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="replyDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitReply" :loading="replySubmitting">提交回复</el-button>
            </template>
        </el-dialog>

        <!-- 新增广告管理对话框 -->
        <el-dialog v-model="adDialogVisible" :title="`广告人群管理 - ${currentCard?.name}`" width="1000px"
            :close-on-click-modal="false" class="ad-audience-dialog">

            <!-- Tab标签页 -->
            <el-tabs v-model="activePlatform" class="platform-tabs" @tab-click="handlePlatformChange">
                <el-tab-pane label="腾讯广告" name="tx">
                    <div v-if="activePlatform === 'tx'">
                        <!-- 腾讯广告子Tab -->
                        <el-tabs v-model="activeTxTab" type="border-card" @tab-change="handleTxTabChange">
                            <el-tab-pane label="OpenID人群绑定" name="openid">
                                <TxAudienceTab ref="openidTab" :card="currentCard" :bind-type="'openid'"
                                    :platform="'tx'" @refresh="fetchCardAudiences" />
                            </el-tab-pane>
                            <el-tab-pane label="手机号人群绑定" name="mobile">
                                <TxAudienceTab ref="mobileTab" :card="currentCard" :bind-type="'mobile'"
                                    :platform="'tx'" @refresh="fetchCardAudiences" />
                            </el-tab-pane>
                        </el-tabs>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="抖音广告" name="douyin">
                    <div class="coming-soon">
                        <el-result icon="info" title="功能开发中" sub-title="程序猿正在紧急开发中···，请稍后！">
                            <template #extra>
                                <el-button type="primary" @click="handleContact">联系客服</el-button>
                            </template>
                        </el-result>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="百度广告" name="baidu">
                    <div class="coming-soon">
                        <el-result icon="info" title="功能开发中" sub-title="程序猿正在紧急开发中···，请稍后！">
                            <template #extra>
                                <el-button type="primary" @click="handleContact">联系客服</el-button>
                            </template>
                        </el-result>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="小红书广告" name="xiaohongshu">
                    <div class="coming-soon">
                        <el-result icon="info" title="功能开发中" sub-title="程序猿正在紧急开发中···，请稍后！">
                            <template #extra>
                                <el-button type="primary" @click="handleContact">联系客服</el-button>
                            </template>
                        </el-result>
                    </div>
                </el-tab-pane>
            </el-tabs>

            <!-- 已绑定人群列表 -->
            <div class="bound-audiences-section">
                <h3 class="section-title">已绑定人群</h3>
                <el-table :data="boundAudiences" stripe style="width: 100%" v-loading="audienceLoading" height="300">
                    <el-table-column label="平台" width="100">
                        <template #default="{ row }">
                            <el-tag :type="getPlatformTagType(row.platform)" size="small">
                                {{ getPlatformText(row.platform) }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column prop="audience_name" label="人群名称" width="200" show-overflow-tooltip />

                    <el-table-column label="绑定类型" width="120">
                        <template #default="{ row }">
                            <el-tag :type="row.bind_type === 'openid' ? 'success' : 'primary'" size="small">
                                {{ row.bind_type === 'openid' ? 'OpenID' : '手机号' }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column label="人群状态" width="100">
                        <template #default="{ row }">
                            <el-tag v-if="row.audience_status" :type="getStatusTagType(row.audience_status)"
                                size="small">
                                {{ getStatusText(row.audience_status) }}
                            </el-tag>
                            <span v-else class="empty-value">未知</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="用户数量" width="100">
                        <template #default="{ row }">
                            <span v-if="row.user_count > 0">{{ row.user_count }} 人</span>
                            <span v-else class="empty-value">暂无</span>
                        </template>
                    </el-table-column>

                    <el-table-column prop="bind_time" label="绑定时间" width="180" />

                    <el-table-column label="操作" width="120" fixed="right">
                        <template #default="scope">
                            <el-button type="danger" size="small" @click="unbindAudience(scope.row)"
                                :loading="unbindLoading[scope.row.id]">
                                <el-icon>
                                    <Unlock />
                                </el-icon>
                                解绑
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 分页 -->
                <div class="pagination-container" v-if="audiencePagination.total > 0">
                    <el-pagination v-model:current-page="audiencePagination.page"
                        v-model:page-size="audiencePagination.pageSize" :total="audiencePagination.total"
                        :page-sizes="[5, 10, 20]" layout="total, sizes, prev, pager, next, jumper"
                        @size-change="fetchCardAudiences" @current-change="fetchCardAudiences" />
                </div>
            </div>

            <template #footer>
                <el-button @click="adDialogVisible = false">关闭</el-button>
            </template>
        </el-dialog>

        <!-- 新增/编辑名片对话框 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" width="800px" :close-on-click-modal="false">
            <el-form ref="cardForm" :model="cardForm" label-width="120px" :rules="formRules" label-position="top">
                <el-row :gutter="20">
                    <el-col :span="12">
                        <!-- 替换原来的关联用户表单项 -->
                        <el-form-item label="关联用户" prop="user_id">
                            <el-select v-model="cardForm.user_id" placeholder="请选择关联用户" filterable style="width: 100%"
                                @change="handleUserChange" :loading="userLoading" :disabled="isEditMode">
                                <el-option v-for="user in userList" :key="user.id"
                                    :label="`${user.nickname || '未设置昵称'} (${user.phone})`" :value="user.id">
                                    <div class="user-option">
                                        <el-avatar :size="24" :src="user.avatar" style="margin-right: 10px;">
                                            {{ user.nickname ? user.nickname.charAt(0) : 'U' }}
                                        </el-avatar>
                                        <span>{{ user.nickname || '未设置昵称' }}</span>
                                        <span style="color: #909399; margin-left: 10px;">{{ user.phone }}</span>
                                    </div>
                                </el-option>
                            </el-select>
                            <div class="form-tip">选择已注册的用户关联名片{{ isEditMode ? '（创建后不可修改）' : '' }}</div>
                        </el-form-item>

                        <el-form-item label="关联TX广告">
                            <el-select v-model="cardForm.gzh_id" placeholder="请选择TX广告授权" filterable style="width: 100%"
                                :loading="gzhLoading" clearable>
                                <el-option v-for="gzh in gzhList" :key="gzh.id"
                                    :label="`${gzh.ghz_name} (${gzh.access_token})`" :value="gzh.id">
                                    <div class="gzh-option">
                                        <span>{{ gzh.ghz_name }}</span>
                                        <span style="color: #909399; margin-left: 10px;">{{ gzh.access_token }}</span>
                                    </div>
                                </el-option>
                            </el-select>
                            <div class="form-tip">选择已授权的TX广告（可选）</div>
                        </el-form-item>

                        <el-form-item label="姓名" prop="name">
                            <el-input v-model="cardForm.name" placeholder="请输入姓名" size="large" />
                        </el-form-item>

                        <el-form-item label="职位">
                            <el-input v-model="cardForm.title" placeholder="请输入职位" size="large" />
                        </el-form-item>

                        <el-form-item label="公司">
                            <el-input v-model="cardForm.company" placeholder="请输入公司名称" size="large" />
                        </el-form-item>

                        <el-form-item label="标签">
                            <el-input v-model="cardForm.tag" placeholder="如：创始人、合伙人等" size="large" />
                        </el-form-item>
                    </el-col>

                    <el-col :span="12">
                        <!-- 在名片对话框的表单中，替换原来的头像URL表单项 -->
                        <el-form-item label="头像">
                            <div class="avatar-upload">
                                <!-- 显示当前头像 -->
                                <div class="avatar-preview" v-if="cardForm.avatar">
                                    <el-image :src="cardForm.avatar" class="avatar-image"
                                        :preview-src-list="[cardForm.avatar]" fit="cover" />
                                    <div class="avatar-actions">
                                        <el-button type="danger" size="small" @click="removeAvatar">
                                            <el-icon>
                                                <Delete />
                                            </el-icon>
                                            删除
                                        </el-button>
                                    </div>
                                </div>

                                <!-- 上传组件 -->
                                <el-upload class="avatar-uploader" :auto-upload="false" :show-file-list="false"
                                    :on-change="handleAvatarChange" accept="image/*" :disabled="avatarUploadLoading">
                                    <el-button type="primary" :loading="avatarUploadLoading">
                                        <el-icon>
                                            <Upload />
                                        </el-icon>
                                        {{ cardForm.avatar ? '更换头像' : '上传头像' }}
                                    </el-button>
                                    <template #tip>
                                        <div class="el-upload__tip">
                                            支持 jpg、png、gif 格式，大小不超过 20MB
                                        </div>
                                    </template>
                                </el-upload>
                            </div>
                        </el-form-item>

                        <el-form-item label="电话">
                            <el-input v-model="cardForm.phone" placeholder="请输入联系电话" size="large" />
                        </el-form-item>

                        <el-form-item label="邮箱">
                            <el-input v-model="cardForm.email" placeholder="请输入邮箱地址" size="large" />
                        </el-form-item>

                        <el-form-item label="微信号">
                            <el-input v-model="cardForm.wechat_id" placeholder="请输入微信号" size="large" />
                        </el-form-item>

                        <el-form-item label="视频号">
                            <el-input v-model="cardForm.video_account" placeholder="请输入视频号" size="large" />
                        </el-form-item>

                        <el-form-item label="个人主页">
                            <el-input v-model="cardForm.personal_url" placeholder="请输入个人主页链接" size="large" />
                        </el-form-item>

                        <el-form-item label="地址">
                            <el-input v-model="cardForm.address" placeholder="请输入地址" type="textarea" :rows="2" />
                        </el-form-item>
                    </el-col>
                </el-row>

                <el-divider content-position="left">数据统计</el-divider>

                <el-row :gutter="20">
                    <el-col :span="8">
                        <el-form-item label="行业经验（年）">
                            <el-input-number v-model="cardForm.experience_years" :min="0" :max="50" size="large" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="服务客户数">
                            <el-input-number v-model="cardForm.served_customers" :min="0" :max="10000" size="large" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="满意度（%）">
                            <el-input-number v-model="cardForm.satisfaction_rate" :min="0" :max="100" :precision="1"
                                size="large" />
                        </el-form-item>
                    </el-col>
                </el-row>

                <el-form-item label="公开状态">
                    <el-switch v-model="cardForm.is_public" active-text="公开" inactive-text="私密" />
                    <div class="form-tip">公开的名片可以通过链接访问</div>
                </el-form-item>

                <el-form-item label="排序">
                    <el-input-number v-model="cardForm.sort_order" :min="0" :max="999" size="large" />
                    <div class="form-tip">数字越小排序越靠前</div>
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitCardForm" :loading="submitting">确认</el-button>
            </template>
        </el-dialog>

        <!-- 产品管理对话框 -->
        <el-dialog v-model="productDialogVisible" title="产品管理" width="900px" :close-on-click-modal="false">
            <div class="product-header">
                <h3>{{ currentCard?.name }} - 产品列表</h3>
                <el-button type="primary" @click="openAddProductDialog">
                    <el-icon>
                        <Plus />
                    </el-icon>
                    添加产品
                </el-button>
            </div>

            <el-table :data="productList" stripe style="width: 100%; margin-top: 20px" v-loading="productLoading">
                <el-table-column label="产品图片" width="100" align="center">
                    <template #default="{ row }">
                        <el-image :src="row.product_image" :alt="row.product_title"
                            style="width: 60px; height: 60px; border-radius: 4px;">
                            <template #error>
                                <div class="image-slot">无图</div>
                            </template>
                        </el-image>
                    </template>
                </el-table-column>

                <el-table-column prop="product_title" label="产品标题" width="200" />
                <el-table-column prop="product_desc" label="产品描述" show-overflow-tooltip />
                <el-table-column prop="product_price" label="价格" width="120" align="center" />
                <el-table-column label="评分" width="100" align="center">
                    <template #default="{ row }">
                        <el-rate v-model="row.product_rating" disabled show-score text-color="#ff9900"
                            score-template="{value} 分" />
                    </template>
                </el-table-column>
                <el-table-column prop="product_tag" label="标签" width="120" align="center">
                    <template #default="{ row }">
                        <el-tag v-if="row.product_tag" type="success" size="small">{{ row.product_tag }}</el-tag>
                        <span v-else class="empty-value">无</span>
                    </template>
                </el-table-column>
                <el-table-column label="状态" width="80" align="center">
                    <template #default="{ row }">
                        <el-tag :type="row.is_active ? 'success' : 'info'" size="small">
                            {{ row.is_active ? '激活' : '禁用' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="sort_order" label="排序" width="80" align="center" />

                <el-table-column label="操作" width="150" fixed="right">
                    <template #default="scope">
                        <el-button type="primary" size="small" @click="openEditProductDialog(scope.row)">
                            <el-icon>
                                <Edit />
                            </el-icon>
                        </el-button>
                        <el-button type="danger" size="small" @click="handleDeleteProduct(scope.row.id)">
                            <el-icon>
                                <Delete />
                            </el-icon>
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <template #footer>
                <el-button @click="productDialogVisible = false">关闭</el-button>
            </template>
        </el-dialog>

        <!-- 添加/编辑产品对话框 -->
        <el-dialog v-model="productFormDialogVisible" :title="productFormTitle" width="800px"
            :close-on-click-modal="false">
            <el-form ref="productForm" :model="productForm" label-width="100px" :rules="productFormRules">

                <!-- 产品标题 -->
                <el-form-item label="产品标题" prop="product_title">
                    <el-input v-model="productForm.product_title" placeholder="请输入产品标题" />
                </el-form-item>

                <!-- 产品图片 -->
                <el-form-item label="产品图片">
                    <div class="image-upload">
                        <div class="image-preview" v-if="productForm.product_image">
                            <el-image :src="productForm.product_image" class="product-image"
                                :preview-src-list="[productForm.product_image]" fit="cover" />
                            <div class="image-actions">
                                <el-button type="danger" size="small" @click="removeProductImage"><el-icon>
                                        <Delete />
                                    </el-icon>删除</el-button>
                            </div>
                        </div>
                        <el-upload class="image-uploader" :auto-upload="false" :show-file-list="false"
                            :on-change="handleProductImageChange" accept="image/*"
                            :disabled="productImageUploadLoading">
                            <el-button type="primary" :loading="productImageUploadLoading"><el-icon>
                                    <Upload />
                                </el-icon>{{ productForm.product_image ? '更换图片' : '上传图片' }}</el-button>
                            <template #tip>
                                <div class="el-upload__tip">支持 jpg、png、gif 格式，大小不超过 20MB</div>
                            </template>
                        </el-upload>
                    </div>
                </el-form-item>
                <!-- 商品图集 -->
                <el-form-item label="商品图集">
                    <div style="display: flex; flex-direction: column; width: 100%;">
                        <!-- 上传按钮 - 在上方 -->
                        <div style="margin-bottom: 12px;">
                            <el-upload :auto-upload="false" :show-file-list="false"
                                :on-change="handleProductImagesChange" accept="image/*" multiple
                                :disabled="productImagesUploadLoading">
                                <el-button type="primary" :loading="productImagesUploadLoading">
                                    <el-icon>
                                        <Upload />
                                    </el-icon>上传图片
                                </el-button>
                            </el-upload>
                        </div>

                        <!-- 图片画廊 - 在下方 -->
                        <div class="product-images-gallery"
                            v-if="productForm.product_images && productForm.product_images.length > 0">
                            <div v-for="(img, idx) in productForm.product_images" :key="idx" class="gallery-item">
                                <el-image :src="img" style="width:80px;height:80px;border-radius:4px;" fit="cover"
                                    :preview-src-list="[img]" />
                                <el-button type="danger" size="small" @click="productForm.product_images.splice(idx, 1)"
                                    style="margin-top:4px;">删除</el-button>
                            </div>
                        </div>
                    </div>
                </el-form-item>
                <!-- 产品描述 -->
                <el-form-item label="产品描述">
                    <el-input v-model="productForm.product_desc" placeholder="请输入产品描述" type="textarea" :rows="3" />
                </el-form-item>

                <!-- 产品详情（富文本）- 对接OSS上传 -->
                <!-- 产品详情（富文本）-->
                <el-form-item label="产品详情">
                    <div class="quill-editor-wrapper" v-if="productFormDialogVisible">
                        <QuillEditor ref="productQuillEditor" v-model:content="productForm.product_detail"
                            contentType="html" theme="snow" :toolbar="productToolbar" @ready="onProductQuillReady" />
                    </div>
                </el-form-item>

                <!-- 联系类型 -->
                <el-divider content-position="left">联系类型</el-divider>
                <el-form-item label="联系类型">
                    <el-select v-model="productForm.contact_type" placeholder="请选择联系类型" style="width: 100%;"
                        @change="handleContactTypeChange" clearable>
                        <el-option label="扫码联系" value="qrcode" />
                        <el-option label="电话联系" value="phone" />
                        <el-option label="小程序跳转" value="miniprogram" />
                        <el-option label="链接跳转" value="link" />
                    </el-select>
                    <div class="form-tip">选择产品联系的类型</div>
                </el-form-item>

                <!-- 扫码联系 - 上传二维码 -->
                <el-form-item v-if="productForm.contact_type === 'qrcode'" label="联系二维码">
                    <div class="image-upload">
                        <div class="image-preview" v-if="contactQrcodeUrl">
                            <el-image :src="contactQrcodeUrl" style="width:100px;height:100px;border-radius:4px;"
                                fit="cover" :preview-src-list="[contactQrcodeUrl]" />
                            <el-button type="danger" size="small"
                                @click="contactQrcodeUrl = ''; productForm.contact_value = ''">删除</el-button>
                        </div>
                        <el-upload :auto-upload="false" :show-file-list="false" :on-change="handleContactQrcodeChange"
                            accept="image/*">
                            <el-button type="primary"><el-icon>
                                    <Upload />
                                </el-icon>{{ contactQrcodeUrl ? '更换' : '上传二维码' }}</el-button>
                        </el-upload>
                    </div>
                    <div class="form-tip">上传联系二维码，用户扫码即可联系</div>
                </el-form-item>

                <!-- 电话联系 -->
                <el-form-item v-if="productForm.contact_type === 'phone'" label="联系电话">
                    <el-input v-model="contactPhone" placeholder="请输入联系电话" />
                    <div class="form-tip">用户点击后可拨打电话</div>
                </el-form-item>

                <!-- 小程序跳转 -->
                <el-form-item v-if="productForm.contact_type === 'miniprogram'" label="小程序AppID">
                    <el-input v-model="contactAppId" placeholder="请输入小程序AppID" />
                </el-form-item>
                <el-form-item v-if="productForm.contact_type === 'miniprogram'" label="小程序路径">
                    <el-input v-model="contactPath" placeholder="如：pages/index/index" />
                    <div class="form-tip">用户点击后跳转到指定小程序页面</div>
                </el-form-item>

                <!-- 链接跳转 -->
                <el-form-item v-if="productForm.contact_type === 'link'" label="跳转链接">
                    <el-input v-model="contactLink" placeholder="请输入跳转链接" />
                    <div class="form-tip">用户点击后跳转到指定链接</div>
                </el-form-item>

                <el-divider content-position="left">其他信息</el-divider>

                <el-row :gutter="20">
                    <el-col :span="8">
                        <el-form-item label="价格">
                            <el-input v-model="productForm.product_price" placeholder="如：¥199、免费、定制等" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="产品标签">
                            <el-input v-model="productForm.product_tag" placeholder="如：热销、新品、推荐等" />
                        </el-form-item>
                    </el-col>
                </el-row>

                <el-row :gutter="20">
                    <el-col :span="8">
                        <el-form-item label="评分">
                            <el-rate v-model="productForm.product_rating" show-score allow-half :max="5"
                                text-color="#ff9900" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="排序">
                            <el-input-number v-model="productForm.sort_order" :min="0" :max="999" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="状态">
                            <el-switch v-model="productForm.is_active" active-text="激活" inactive-text="禁用" />
                        </el-form-item>
                    </el-col>
                </el-row>
            </el-form>
            <template #footer>
                <el-button @click="productFormDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitProductForm" :loading="productSubmitting">确认</el-button>
            </template>
        </el-dialog>

        <!-- 二维码预览弹窗 -->
        <el-dialog v-model="qrcodePreviewVisible" :title="qrcodePreviewTitle" width="400px" align-center
            :close-on-click-modal="false">
            <div class="qrcode-preview-container">
                <img :src="qrcodePreviewUrl" alt="小程序二维码" class="preview-qrcode-image" />
                <div class="preview-actions">
                    <el-button type="primary"
                        @click="downloadQrcode(qrcodePreviewUrl, qrcodePreviewTitle.replace(' - 小程序二维码', ''))">
                        <el-icon>
                            <Download />
                        </el-icon>
                        下载
                    </el-button>
                    <el-button @click="copyUrl(qrcodePreviewUrl)">
                        <el-icon>
                            <DocumentCopy />
                        </el-icon>
                        复制链接
                    </el-button>
                </div>
                <div class="preview-tip">
                    <el-icon>
                        <InfoFilled />
                    </el-icon>
                    此二维码永久有效，扫描可直接进入小程序
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
    Search, Plus, Edit, Delete, Refresh, Star, StarFilled, Goods, Upload,
    Promotion, Unlock, Picture, Download, DocumentCopy, Loading, InfoFilled, Setting
} from '@element-plus/icons-vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

// 导入新组件
import TxAudienceTab from '@/components/TxAudienceTab.vue';

import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils';
import { ElLoading } from 'element-plus';

export default {
    name: 'BusinessCardManagement',
    components: {
        Search, Plus, Edit, Delete, Refresh, Star, StarFilled, Goods, Upload,
        Promotion, Unlock, Picture, Download, DocumentCopy, Loading, InfoFilled, QuillEditor, Setting,
        TxAudienceTab
    },
    data() {
        return {
            loading: false,
            submitting: false,
            userLoading: false,
            gzhLoading: false,
            productLoading: false,
            productSubmitting: false,
            dialogVisible: false,
            productDialogVisible: false,
            productFormDialogVisible: false,
            dialogTitle: '新增名片',
            productFormTitle: '添加产品',
            isEditMode: false,
            isProductEditMode: false,
            searchForm: {
                name: ''
            },

            // 视频文件上传
            videoFileUploading: false,

            // 音频文件上传
            audioFileUploading: false,

            // 新增小程序二维码相关数据
            miniprogramConfig: null, // 小程序配置
            qrcodeLoading: false, // 全局二维码加载状态

            // 小程序二维码预览弹窗
            qrcodePreviewVisible: false,
            qrcodePreviewUrl: '',
            qrcodePreviewTitle: '',
            // 新增广告相关数据
            adDialogVisible: false,
            activePlatform: 'tx',
            activeTxTab: 'openid',
            currentCard: null,

            // 已绑定人群数据
            boundAudiences: [],
            audienceLoading: false,
            audiencePagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },
            unbindLoading: {},
            cardForm: {
                id: null,
                user_id: null,
                gzh_id: null,
                name: '',
                title: '',
                company: '',
                tag: '',
                phone: '',
                email: '',
                address: '',
                wechat_id: '',
                video_account: '',
                personal_url: '',
                avatar: '',
                experience_years: 0,
                served_customers: 0,
                satisfaction_rate: 0,
                is_public: true,
                sort_order: 0
            },
            productForm: {
                id: null,
                product_title: '',
                product_desc: '',
                product_detail: '',  // 新增：富文本详情
                product_image: '',
                product_images: [],  // 新增：图集
                product_price: '',
                product_rating: 0,
                product_tag: '',
                contact_type: '',    // 新增：联系方式类型
                contact_value: '',   // 新增：联系方式值
                product_url: '',
                sort_order: 0,
                is_active: true
            },
            formRules: {
                user_id: [
                    { required: true, message: '请选择关联用户', trigger: 'change' }
                ],
                name: [
                    { required: true, message: '请输入姓名', trigger: 'blur' },
                    { min: 2, max: 20, message: '姓名长度在 2 到 20 个字符', trigger: 'blur' }
                ],
                email: [
                    { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
                ]
            },
            productFormRules: {
                product_title: [
                    { required: true, message: '请输入产品标题', trigger: 'blur' }
                ]
            },
            cardList: [],
            userList: [],
            gzhList: [],
            productList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },
            stats: {
                total: 0,
                public: 0
            },



            avatarUploadLoading: false,
            productImageUploadLoading: false,

            // ========== 富文本编辑器配置 ==========
            quillOptions: {
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        ['blockquote', 'code-block'],
                        [{ 'header': 1 }, { 'header': 2 }],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        [{ 'script': 'sub' }, { 'script': 'super' }],
                        [{ 'indent': '-1' }, { 'indent': '+1' }],
                        [{ 'direction': 'rtl' }],
                        [{ 'size': ['small', false, 'large', 'huge'] }],
                        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                        [{ 'color': [] }, { 'background': [] }],
                        [{ 'font': [] }],
                        [{ 'align': [] }],
                        ['clean'],
                        ['link', 'image', 'video']
                    ]
                },
                placeholder: '请输入内容...'
            },


            // ========== 自定义管理对话框 ==========
            customDialogVisible: false,
            customActiveTab: 'sections',
            homepageSubTab: 'profile',

            // 板块配置
            sectionList: [],
            sectionSaving: false,
            defaultSectionNames: {
                products: '产品列表',
                profile: '个人简介',
                tags: '个性标签',
                companies: '我的企业',
                videos: '视频展示',
                images: '图片展示',
                audios: '音频展示'
            },

            // 个人简介
            profileContent: '',
            profileSaving: false,

            // 个性标签
            tagList: [],
            tagLoading: false,
            tagDialogVisible: false,
            tagForm: { tag_name: '', virtual_likes: 0, sort_order: 0 },
            tagSubmitting: false,

            // 我的企业
            companyList: [],
            companyLoading: false,
            companyDialogVisible: false,
            companyDialogTitle: '添加企业',
            isCompanyEdit: false,
            companyForm: { id: null, logo: '', company_name: '', industry: '', website: '', description: '', sort_order: 0 },
            companySubmitting: false,
            companyLogoUploading: false,

            // 视频展示
            videoList: [],
            videoLoading: false,
            videoDialogVisible: false,
            videoDialogTitle: '添加视频',
            isVideoEdit: false,
            videoForm: { id: null, video_url: '', video_cover: '', video_name: '', video_desc: '', virtual_likes: 0, virtual_comments: 0, virtual_shares: 0, sort_order: 0 },
            videoSubmitting: false,
            videoCoverUploading: false,

            // 图片展示
            imageList: [],
            imageLoading: false,
            imageDialogVisible: false,
            imageDialogTitle: '添加图片',
            isImageEdit: false,
            imageForm: { id: null, image_url: '', image_name: '', image_desc: '', sort_order: 0 },
            imageSubmitting: false,
            imageUploadLoading: false,

            // 音频展示
            audioList: [],
            audioLoading: false,
            audioDialogVisible: false,
            audioDialogTitle: '添加音频',
            isAudioEdit: false,
            audioForm: { id: null, audio_url: '', audio_name: '', audio_desc: '', sort_order: 0 },
            audioSubmitting: false,

            // 产品图集上传
            productImagesUploadLoading: false,

            productQuillOptions: {
                modules: {
                    toolbar: {
                        container: [
                            ['bold', 'italic', 'underline', 'strike'],
                            ['blockquote', 'code-block'],
                            [{ 'header': 1 }, { 'header': 2 }],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            [{ 'script': 'sub' }, { 'script': 'super' }],
                            [{ 'indent': '-1' }, { 'indent': '+1' }],
                            [{ 'size': ['small', false, 'large', 'huge'] }],
                            [{ 'color': [] }, { 'background': [] }],
                            [{ 'font': [] }],
                            [{ 'align': [] }],
                            ['clean'],
                            ['link', 'image', 'video']
                        ]
                    }
                },
                placeholder: '请输入产品详情...'
            },
            // 产品富文本工具栏配置
            productToolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote', 'code-block'],
                [{ 'header': 1 }, { 'header': 2 }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'script': 'sub' }, { 'script': 'super' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }],
                [{ 'size': ['small', false, 'large', 'huge'] }],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'font': [] }],
                [{ 'align': [] }],
                ['clean'],
                ['link', 'image', 'video']
            ],

            // 关于我们富文本工具栏配置
            aboutUsToolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote', 'code-block'],
                [{ 'header': 1 }, { 'header': 2 }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'script': 'sub' }, { 'script': 'super' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }],
                [{ 'size': ['small', false, 'large', 'huge'] }],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'font': [] }],
                [{ 'align': [] }],
                ['clean'],
                ['link', 'image', 'video']
            ],
            // 联系方式相关
            contactQrcodeUrl: '',
            contactPhone: '',
            contactAppId: '',
            contactPath: '',
            contactLink: '',

            // 在线客服
            customerServiceList: [],
            csLoading: false,
            csDialogVisible: false,
            csDialogTitle: '添加客服',
            isCsEdit: false,
            csForm: { id: null, service_type: 'wechat', service_name: '', service_account: '', qr_code: '', service_url: '', description: '', service_time: '', sort_order: 0, is_active: 1 },
            csSubmitting: false,
            csQrcodeUploading: false,

            // 意见反馈
            feedbackList: [],
            feedbackLoading: false,
            feedbackPagination: { page: 1, pageSize: 10, total: 0 },
            replyDialogVisible: false,
            replyForm: { id: null, title: '', content: '', reply_content: '', user_nickname: '', user_phone: '' },
            replySubmitting: false,

            // 关于我们
            aboutUsForm: { title: '', content: '', logo: '', company_name: '', company_address: '', company_phone: '', company_email: '', official_website: '', social_links: [], images: [] },
            aboutUsSaving: false,
            aboutUsLogoUploading: false,
            aboutUsImageUploading: false
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        },
        primaryCardName() {
            const primaryCard = this.cardList.find(card => card.is_primary)
            return primaryCard ? primaryCard.name : '未设置'
        },
        // 检查是否已配置小程序
        hasMiniprogramConfig() {
            return this.miniprogramConfig &&
                this.miniprogramConfig.appid &&
                this.miniprogramConfig.secret
        }
    },
    mounted() {
        this.fetchCardList()
        this.fetchUserList()
        this.fetchGzhList()
        this.fetchMiniprogramConfig() // 新增：获取小程序配置

    },
    methods: {


        // 关于我们富文本编辑器准备就绪
        onAboutUsQuillReady(quill) {
            const toolbar = quill.getModule('toolbar');
            if (!toolbar) return;

            const self = this;

            // 重新绑定图片上传 handler
            toolbar.addHandler('image', function () {
                self.aboutUsQuillImageHandler(quill);
            });

            // 重新绑定视频上传 handler
            toolbar.addHandler('video', function () {
                self.aboutUsQuillVideoHandler(quill);
            });
        },

        // 关于我们富文本图片上传
        aboutUsQuillImageHandler(quill) {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.click();

            input.onchange = async () => {
                const file = input.files[0];
                if (!file) return;

                if (!file.type.startsWith('image/')) {
                    this.$message.error('只能上传图片文件');
                    return;
                }

                if (file.size > 5 * 1024 * 1024) {
                    this.$message.error('图片大小不能超过 5MB');
                    return;
                }

                const loading = ElLoading.service({
                    lock: true,
                    text: '图片上传中...',
                    background: 'rgba(0, 0, 0, 0.7)'
                });

                try {
                    const url = await this.uploadImage(file);
                    const range = quill.getSelection(true);
                    quill.insertEmbed(range.index, 'image', url);
                    quill.setSelection(range.index + 1);
                    loading.close();
                    this.$message.success('图片上传成功');
                } catch (error) {
                    loading.close();
                    this.$message.error(error.message || '图片上传失败');
                    console.error('图片上传失败:', error);
                }
            };
        },

        // 关于我们富文本视频上传
        aboutUsQuillVideoHandler(quill) {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'video/*');
            input.click();

            input.onchange = async () => {
                const file = input.files[0];
                if (!file) return;

                const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
                if (!allowedTypes.includes(file.type)) {
                    this.$message.error('仅支持 MP4、MOV、AVI、WebM 格式的视频');
                    return;
                }

                if (file.size > 100 * 1024 * 1024) {
                    this.$message.error('视频大小不能超过 100MB');
                    return;
                }

                const loading = ElLoading.service({
                    lock: true,
                    text: '视频上传中，请耐心等待...',
                    background: 'rgba(0, 0, 0, 0.7)'
                });

                try {
                    const url = await this.uploadVideo(file);
                    const range = quill.getSelection(true);
                    quill.insertEmbed(range.index, 'video', url);
                    quill.setSelection(range.index + 1);
                    loading.close();
                    this.$message.success('视频上传成功');
                } catch (error) {
                    loading.close();
                    this.$message.error(error.message || '视频上传失败');
                    console.error('视频上传失败:', error);
                }
            };
        },

        // 处理视频文件上传
        async handleVideoFileChange(file) {
            try {
                this.videoFileUploading = true;
                this.$message.info('视频上传中，请耐心等待...');

                // 验证文件类型
                const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
                if (!allowedTypes.includes(file.raw.type)) {
                    throw new Error('仅支持 MP4、MOV、AVI、WebM 格式的视频');
                }

                // 验证文件大小 (100MB)
                if (file.raw.size > 100 * 1024 * 1024) {
                    throw new Error('视频大小不能超过 100MB');
                }

                // 上传到 OSS
                const url = await this.uploadVideo(file.raw);
                this.videoForm.video_url = url;
                this.$message.success('视频上传成功');
            } catch (error) {
                this.$message.error(error.message || '视频上传失败');
                console.error('视频上传错误:', error);
            } finally {
                this.videoFileUploading = false;
            }
        },

        // 处理音频文件上传
        async handleAudioFileChange(file) {
            try {
                this.audioFileUploading = true;
                this.$message.info('音频上传中，请稍候...');

                // 验证文件类型
                const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/x-m4a'];
                if (!allowedTypes.includes(file.raw.type)) {
                    throw new Error('仅支持 MP3、WAV、OGG、M4A 格式的音频');
                }

                // 验证文件大小 (20MB)
                if (file.raw.size > 20 * 1024 * 1024) {
                    throw new Error('音频大小不能超过 20MB');
                }

                // 上传到 OSS
                const url = await this.uploadAudio(file.raw);
                this.audioForm.audio_url = url;
                this.$message.success('音频上传成功');
            } catch (error) {
                this.$message.error(error.message || '音频上传失败');
                console.error('音频上传错误:', error);
            } finally {
                this.audioFileUploading = false;
            }
        },

        // 上传音频到 OSS
        async uploadAudio(file) {
            try {
                const signature = await getUploadSignature('audio');
                const res = await uploadToOSS(file, signature);
                return res.url;
            } catch (error) {
                console.error('音频上传失败:', error);
                throw error;
            }
        },
        // 处理头像选择变化
        async handleAvatarChange(file) {
            try {
                this.avatarUploadLoading = true;
                this.$message.info('头像上传中，请稍候...');
                const url = await this.uploadImage(file.raw);
                this.cardForm.avatar = url;
                this.$message.success('头像上传成功');
            } catch (error) {
                this.$message.error(`头像上传失败: ${error.message}`);
                console.error('头像上传错误:', error);
            } finally {
                this.avatarUploadLoading = false;
            }
        },

        // 处理产品图片选择变化
        async handleProductImageChange(file) {
            try {
                this.productImageUploadLoading = true;
                this.$message.info('产品图片上传中，请稍候...');
                const url = await this.uploadImage(file.raw);
                this.productForm.product_image = url;
                this.$message.success('产品图片上传成功');
            } catch (error) {
                this.$message.error(`产品图片上传失败: ${error.message}`);
                console.error('产品图片上传错误:', error);
            } finally {
                this.productImageUploadLoading = false;
            }
        },

        // 获取小程序配置
        async fetchMiniprogramConfig() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/platform_management.php`,
                    {
                        action: 'get_all_settings',
                        store_id: this.userInfo.id
                    }
                );

                if (response.data.data && response.data.data.miniprogram) {
                    this.miniprogramConfig = response.data.data.miniprogram;

                }
            } catch (error) {
                console.error('获取小程序配置失败:', error);
            }
        },

        // 生成小程序二维码
        async generateMiniprogramQrcode(row) {
            try {
                // 设置加载状态
                row.qrcode_loading = true;

                // 修改判断条件：同时检查文件名和URL
                const hasExistingQrcode = !!(row.miniprogram_qrcode_filename || row.miniprogram_qrcode);
                const forceRegenerate = hasExistingQrcode;

                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'generate_miniprogram_qrcode',
                        store_id: this.userInfo.id,
                        card_id: row.id,
                        force: forceRegenerate // 如果已有二维码，则强制重新生成
                    }
                );

                if (response.data.success) {
                    // 更新名片的二维码数据
                    row.miniprogram_qrcode = response.data.qrcode_url;
                    row.miniprogram_qrcode_last_generate_time = response.data.generate_time;
                    row.miniprogram_qrcode_generate_count = response.data.generate_count;

                    this.$message.success(response.data.is_cached ? '已加载缓存的二维码' : '二维码生成成功');

                    // 如果是缓存的二维码，更新按钮文本
                    if (response.data.is_cached && this.$refs.qrcodeButton) {
                        // 可能需要更新按钮状态
                    }
                } else {
                    this.$message.error(response.data.error || '生成失败');
                }
            } catch (error) {
                console.error('生成小程序二维码失败:', error);
                let errorMsg = '生成失败';
                if (error.response && error.response.data && error.response.data.error) {
                    errorMsg = error.response.data.error;
                }
                this.$message.error(errorMsg);
            } finally {
                row.qrcode_loading = false;
            }
        },

        // 预览二维码
        previewQrcode(url, name) {
            this.qrcodePreviewUrl = url;
            this.qrcodePreviewTitle = `${name} - 小程序二维码`;
            this.qrcodePreviewVisible = true;
        },

        // 下载二维码
        downloadQrcode(url, name) {
            try {
                const link = document.createElement('a');
                link.href = url;
                link.download = `小程序二维码_${name || '名片'}_${Date.now()}.png`;
                link.target = '_blank';

                document.body.appendChild(link);
                link.click();

                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                }, 100);

                this.$message.success('二维码下载成功');
            } catch (error) {
                console.error('下载二维码失败:', error);
                this.$message.error('下载失败，请重试');
            }
        },
        // 上传图片到OSS
        async uploadImage(file) {
            try {
                const loading = ElLoading.service({
                    lock: true,
                    text: '图片上传中...',
                    spinner: 'el-icon-loading',
                    background: 'rgba(0, 0, 0, 0.7)'
                });

                // 文件验证
                const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
                const maxSize = 20 * 1024 * 1024; // 20MB

                if (allowedTypes.indexOf(file.type) === -1) {
                    throw new Error(`不支持的文件类型: ${file.type}，仅支持 ${allowedTypes.join(', ')}`);
                }

                if (file.size > maxSize) {
                    throw new Error(`文件大小超过限制 (20MB): ${(file.size / 1024 / 1024).toFixed(2)}MB`);
                }

                const signature = await getUploadSignature(file.type.split('/')[0]);
                const res = await uploadToOSS(file, signature);

                loading.close();
                return res.url;
            } catch (error) {
                ElLoading.service().close();
                console.error('图片上传失败:', error);
                throw error;
            }
        },

        // 删除头像
        removeAvatar() {
            this.cardForm.avatar = '';
        },

        // 删除产品图片
        removeProductImage() {
            this.productForm.product_image = '';
        },

        // 打开广告管理对话框
        openAdDialog(row) {
            this.currentCard = row;
            this.adDialogVisible = true;
            this.activePlatform = 'tx';
            this.activeTxTab = 'openid';
            this.fetchCardAudiences();

            // 延迟加载子组件数据
            this.$nextTick(() => {
                if (this.$refs.openidTab) {
                    this.$refs.openidTab.fetchAvailableAudiences();
                }
            });
        },

        // 获取已绑定人群
        async fetchCardAudiences() {
            if (!this.currentCard) return;

            this.audienceLoading = true;
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'get_card_audiences',
                        store_id: this.userInfo.id,
                        card_id: this.currentCard.id,
                        page: this.audiencePagination.page,
                        page_size: this.audiencePagination.pageSize
                    }
                );

                if (response.data.data) {
                    this.boundAudiences = response.data.data;
                    this.audiencePagination.total = response.data.pagination?.total || 0;
                }
            } catch (error) {
                console.error('获取已绑定人群错误:', error);
                this.boundAudiences = [];
            } finally {
                this.audienceLoading = false;
            }
        },

        // 解绑人群
        async unbindAudience(audience) {
            try {
                await this.$confirm('确定要解绑这个人群吗？', '确认解绑', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                });

                this.unbindLoading = { ...this.unbindLoading, [audience.id]: true };

                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'unbind_audience',
                        store_id: this.userInfo.id,
                        bind_id: audience.id
                    }
                );

                if (response.data.success) {
                    this.$message.success('解绑成功');
                    // 刷新已绑定人群列表
                    this.fetchCardAudiences();

                    // 刷新对应的子组件数据
                    this.$nextTick(() => {
                        if (this.activeTxTab === 'openid' && this.$refs.openidTab) {
                            this.$refs.openidTab.fetchAvailableAudiences();
                        } else if (this.activeTxTab === 'mobile' && this.$refs.mobileTab) {
                            this.$refs.mobileTab.fetchAvailableAudiences();
                        }
                    });
                } else {
                    this.$message.error(response.data.error || '解绑失败');
                }
            } catch (error) {
                if (error !== 'cancel') {
                    this.$message.error('解绑失败');
                    console.error('解绑错误:', error);
                }
            } finally {
                this.unbindLoading = { ...this.unbindLoading, [audience.id]: false };
            }
        },

        // 1. 修改handlePlatformChange方法，添加数据刷新
        handlePlatformChange(tab) {
            if (tab.props.name !== 'tx') {
                this.$message.info('程序猿正在紧急开发中···，请稍后！');
            } else {
                // 切换到腾讯广告Tab时，刷新已绑定人群数据
                this.fetchCardAudiences();
            }
        },

        // 2. 添加处理腾讯子Tab切换的方法
        handleTxTabChange(tabName) {
            // 切换OpenID/手机号Tab时，刷新对应子组件的数据
            this.$nextTick(() => {
                if (tabName === 'openid' && this.$refs.openidTab) {
                    this.$refs.openidTab.fetchAvailableAudiences();
                } else if (tabName === 'mobile' && this.$refs.mobileTab) {
                    this.$refs.mobileTab.fetchAvailableAudiences();
                }
            });
        },

        // 联系客服
        handleContact() {
            this.$message.info('请联系客服获取最新开发进度');
        },

        // 平台标签样式
        getPlatformTagType(platform) {
            const typeMap = {
                'tx': 'primary',
                'douyin': 'danger',
                'baidu': 'warning',
                'xiaohongshu': 'success'
            };
            return typeMap[platform] || 'info';
        },

        getPlatformText(platform) {
            const textMap = {
                'tx': '腾讯广告',
                'douyin': '抖音广告',
                'baidu': '百度广告',
                'xiaohongshu': '小红书'
            };
            return textMap[platform] || platform;
        },

        // 人群状态样式
        getStatusTagType(status) {
            const typeMap = {
                'SUCCESS': 'success',
                'PROCESSING': 'warning',
                'PENDING': 'info',
                'ERROR': 'danger',
                'DELETED': 'danger'
            };
            return typeMap[status] || 'info';
        },

        getStatusText(status) {
            const textMap = {
                'SUCCESS': '活跃',
                'PROCESSING': '处理中',
                'PENDING': '等待中',
                'ERROR': '错误',
                'DELETED': '已删除'
            };
            return textMap[status] || status;
        },

        getAudienceTypeText(type) {
            const textMap = {
                'CUSTOMER_FILE': '客户文件',
                'LOOKALIKE': 'Lookalike',
                'USER_ACTION': '用户行为',
                'KEYWORD': '关键词',
                'COMBINE': '组合'
            };
            return textMap[type] || type;
        },

        // 获取名片列表
        async fetchCardList() {
            this.loading = true
            try {
                const params = {
                    action: 'list',
                    store_id: this.userInfo.id,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize,
                    with_qrcode_info: true  // 新增参数：获取二维码信息
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    params
                )

                if (data.data) {
                    // 转换数据类型
                    this.cardList = data.data.map(card => ({
                        ...card,
                        is_public: Boolean(card.is_public),
                        is_primary: Boolean(card.is_primary),
                        qrcode_loading: false, // 新增：初始化二维码加载状态
                        // 确保 miniprogram_qrcode 字段正确设置
                        miniprogram_qrcode: card.miniprogram_qrcode_url || card.miniprogram_qrcode || '',
                        // 保留其他字段用于判断
                        miniprogram_qrcode_filename: card.miniprogram_qrcode_filename,
                        miniprogram_qrcode_url: card.miniprogram_qrcode_url
                    }))
                    this.pagination.total = data.pagination?.total || 0
                    this.updateStats()
                }
            } catch (error) {
                this.$message.error('获取名片列表失败')
                console.error('获取名片列表错误:', error)
            } finally {
                this.loading = false
            }
        },

        // 获取用户列表
        async fetchUserList() {
            this.userLoading = true
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'get_users',
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.data) {
                    this.userList = response.data.data
                }
            } catch (error) {
                console.error('获取用户列表错误:', error)
            } finally {
                this.userLoading = false
            }
        },

        // 获取TX广告授权列表
        async fetchGzhList() {
            this.gzhLoading = true
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'get_gzh_list',
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.data) {
                    this.gzhList = response.data.data
                }
            } catch (error) {
                console.error('获取TX广告列表错误:', error)
            } finally {
                this.gzhLoading = false
            }
        },

        // 获取产品列表
        async fetchProductList(cardId) {
            this.productLoading = true
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'get_products',
                        store_id: this.userInfo.id,
                        card_id: cardId
                    }
                )

                if (response.data.data) {
                    // 转换数据类型
                    this.productList = response.data.data.map(product => ({
                        ...product,
                        is_active: Boolean(product.is_active),
                        product_rating: parseFloat(product.product_rating) || 0
                    }))
                }
            } catch (error) {
                console.error('获取产品列表错误:', error)
                this.productList = []
            } finally {
                this.productLoading = false
            }
        },

        // 更新统计信息
        updateStats() {
            this.stats.total = this.cardList.length
            this.stats.public = this.cardList.filter(card => card.is_public).length
        },

        // 处理搜索
        handleSearch() {
            this.pagination.page = 1
            if (this.searchForm.name) {
                this.searchCards()
            } else {
                this.fetchCardList()
            }
        },

        // 搜索名片
        async searchCards() {
            this.loading = true
            try {
                const params = {
                    action: 'search',
                    store_id: this.userInfo.id,
                    name: this.searchForm.name,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    params
                )

                if (data.data) {
                    this.cardList = data.data.map(card => ({
                        ...card,
                        is_public: Boolean(card.is_public),
                        is_primary: Boolean(card.is_primary)
                    }))
                    this.pagination.total = data.pagination?.total || 0
                    this.updateStats()
                }
            } catch (error) {
                this.$message.error('搜索失败')
                console.error('搜索错误:', error)
            } finally {
                this.loading = false
            }
        },

        // 用户选择变化
        handleUserChange(userId) {
            const selectedUser = this.userList.find(user => user.id === userId)
            if (selectedUser) {
                this.cardForm.name = selectedUser.nickname || ''
                this.cardForm.avatar = selectedUser.avatar || ''
                this.cardForm.phone = selectedUser.phone || ''
            }
        },

        // 打开新增名片对话框
        openAddDialog() {
            this.dialogTitle = '新增名片'
            this.isEditMode = false
            this.cardForm = {
                id: null,
                user_id: null,
                gzh_id: null,
                name: '',
                title: '',
                company: '',
                tag: '',
                phone: '',
                email: '',
                address: '',
                wechat_id: '',
                video_account: '',
                personal_url: '',
                avatar: '',
                experience_years: 0,
                served_customers: 0,
                satisfaction_rate: 0,
                is_public: true,
                sort_order: 0
            }
            this.dialogVisible = true

            this.$nextTick(() => {
                if (this.$refs.cardForm) {
                    this.$refs.cardForm.clearValidate()
                }
            })
        },

        // 打开编辑名片对话框
        openEditDialog(row) {
            this.dialogTitle = '编辑名片'
            this.isEditMode = true
            this.cardForm = { ...row }
            this.dialogVisible = true

            this.$nextTick(() => {
                if (this.$refs.cardForm) {
                    this.$refs.cardForm.clearValidate()
                }
            })
        },

        // 打开产品管理对话框
        openProductDialog(row) {
            this.currentCard = row
            this.productDialogVisible = true
            this.fetchProductList(row.id)
        },





        // Quill 编辑器准备就绪 - 绑定自定义上传
        onProductQuillReady(quill) {
            const toolbar = quill.getModule('toolbar');
            if (!toolbar) return;

            const self = this;

            // 重新绑定图片上传 handler
            toolbar.addHandler('image', function () {
                self.quillImageHandler(quill);
            });

            // 重新绑定视频上传 handler
            toolbar.addHandler('video', function () {
                self.quillVideoHandler(quill);
            });
        },

        // 打开添加产品对话框
        openAddProductDialog() {
            this.productFormTitle = '添加产品';
            this.isProductEditMode = false;


            // 重置表单...
            this.$nextTick(() => {
                this.productForm = {
                    id: null,
                    product_title: '',
                    product_desc: '',
                    product_detail: '',
                    product_image: '',
                    product_images: [],
                    product_price: '',
                    product_rating: 0,
                    product_tag: '',
                    contact_type: '',
                    contact_value: '',
                    product_url: '',
                    sort_order: 0,
                    is_active: true
                };
                this.contactQrcodeUrl = '';
                this.contactPhone = '';
                this.contactAppId = '';
                this.contactPath = '';
                this.contactLink = '';
                this.productFormDialogVisible = true;

                this.$nextTick(() => {
                    if (this.$refs.productForm) {
                        this.$refs.productForm.clearValidate();
                    }
                });
            });
        },

        openEditProductDialog(row) {
            this.productFormTitle = '编辑产品';
            this.isProductEditMode = true;

            // 先关闭对话框
            if (this.productFormDialogVisible) {
                this.productFormDialogVisible = false;
            }

            // 处理 product_images
            let productImages = [];
            if (row.product_images) {
                if (typeof row.product_images === 'string') {
                    try { productImages = JSON.parse(row.product_images); } catch (e) { productImages = []; }
                } else if (Array.isArray(row.product_images)) {
                    productImages = row.product_images;
                }
            }

            // 处理 contact_value
            let contactValue = '';
            if (row.contact_value) {
                contactValue = typeof row.contact_value === 'object' ? JSON.stringify(row.contact_value) : row.contact_value;
            }

            // 处理 is_active
            let isActive;
            if (typeof row.is_active === 'boolean') isActive = row.is_active;
            else if (typeof row.is_active === 'number') isActive = row.is_active === 1;
            else isActive = parseInt(row.is_active) === 1;

            this.$nextTick(() => {
                this.productForm = {
                    id: row.id,
                    product_title: row.product_title || '',
                    product_desc: row.product_desc || '',
                    product_detail: row.product_detail || '',
                    product_image: row.product_image || '',
                    product_images: productImages,
                    product_price: row.product_price || '',
                    product_rating: parseFloat(row.product_rating) || 0,
                    product_tag: row.product_tag || '',
                    contact_type: row.contact_type || '',
                    contact_value: contactValue,
                    product_url: row.product_url || '',
                    sort_order: parseInt(row.sort_order) || 0,
                    is_active: isActive
                };

                this.contactQrcodeUrl = '';
                this.contactPhone = '';
                this.contactAppId = '';
                this.contactPath = '';
                this.contactLink = '';

                // 回填联系方式
                if (this.productForm.contact_type && contactValue) {
                    try {
                        const val = typeof contactValue === 'string' ? JSON.parse(contactValue) : contactValue;
                        switch (this.productForm.contact_type) {
                            case 'qrcode': this.contactQrcodeUrl = val.qrcode || ''; break;
                            case 'phone': this.contactPhone = val.phone || ''; break;
                            case 'miniprogram':
                                this.contactAppId = val.appid || '';
                                this.contactPath = val.path || '';
                                break;
                            case 'link': this.contactLink = val.url || ''; break;
                        }
                    } catch (e) {
                        //注释
                    }
                }

                this.productFormDialogVisible = true;

                this.$nextTick(() => {
                    if (this.$refs.productForm) {
                        this.$refs.productForm.clearValidate();
                    }
                });
            });
        },

        // 提交名片表单
        async submitCardForm() {
            this.$refs.cardForm.validate(async (valid) => {
                if (valid) {
                    this.submitting = true
                    try {
                        const action = this.isEditMode ? 'update' : 'add'
                        const payload = {
                            ...this.cardForm,
                            store_id: this.userInfo.id
                        }

                        const response = await axios.post(
                            `${config.BASE_URL}/shop/api/business_card_management.php`,
                            {
                                action,
                                ...payload
                            }
                        )

                        if (response.data.error) {
                            this.$message.error(response.data.error)
                        } else {
                            this.$message.success(this.isEditMode ? '更新成功' : '添加成功')
                            this.dialogVisible = false
                            this.fetchCardList()
                        }
                    } catch (error) {
                        let errorMsg = '操作失败'
                        if (error.response && error.response.data && error.response.data.error) {
                            errorMsg = error.response.data.error
                        }
                        this.$message.error(errorMsg)
                        console.error('表单提交错误:', error)
                    } finally {
                        this.submitting = false
                    }
                }
            })
        },


        // ========== 自定义管理相关方法 ==========

        // 打开自定义管理对话框
        async openCustomDialog(row) {
            this.currentCard = row;
            this.customDialogVisible = true;
            this.customActiveTab = 'sections';
            this.homepageSubTab = 'profile';

            // 加载板块配置
            await this.fetchCardSections();
            // 预加载其他数据
            this.fetchProfile();
            this.fetchTags();
            this.fetchCompanies();
            this.fetchVideos();
            this.fetchImages();
            this.fetchAudios();
            this.fetchCustomerServices();
            this.fetchFeedbackList();
            this.fetchAboutUs();
        },

        // 获取默认板块名称
        getDefaultSectionName(sectionKey) {
            return this.defaultSectionNames[sectionKey] || sectionKey;
        },

        // 获取板块配置
        async fetchCardSections() {
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'get_card_sections',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id
                });
                if (data.data) {
                    // 修复：正确处理 is_visible 为 0 的情况
                    this.sectionList = data.data.map(s => ({
                        ...s,
                        is_visible: s.is_visible === 1 || s.is_visible === true ? 1 : 0
                    }));
                }
            } catch (error) {
                console.error('获取板块配置失败:', error);
            }
        },

        async saveSectionName(row) {
            try {
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'update_section_name',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    section_key: row.section_key,
                    section_name: row.section_name
                });
                this.$message.success('板块名称保存成功');
            } catch (error) {
                this.$message.error('保存失败');
            }
        },

        async saveAllSections() {
            this.sectionSaving = true;
            try {
                // 确保传递正确的数据格式
                const sectionsToSave = this.sectionList.map(s => ({
                    id: s.id,
                    section_key: s.section_key,
                    section_name: s.section_name || '',
                    is_visible: s.is_visible === 1 ? 1 : 0,  // 确保是数字
                    sort_order: parseInt(s.sort_order) || 0
                }));

                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'save_card_sections',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    sections: JSON.stringify(sectionsToSave)
                });
                this.$message.success('全部排序保存成功');
            } catch (error) {
                this.$message.error('保存失败');
            } finally {
                this.sectionSaving = false;
            }
        },

        async saveSectionVisibility(row) {
            try {
                // 传递单个板块的配置
                const sectionData = {
                    id: row.id,
                    section_key: row.section_key,
                    is_visible: row.is_visible, // 已经是 0 或 1
                    sort_order: row.sort_order,
                    section_name: row.section_name || ''
                };

                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'save_card_sections',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    sections: JSON.stringify([sectionData])
                });
                this.$message.success(row.is_visible ? '板块已显示' : '板块已隐藏');
            } catch (error) {
                // 失败时恢复原值
                row.is_visible = row.is_visible === 1 ? 0 : 1;
                this.$message.error('操作失败');
            }
        },

        // ========== 个人简介 ==========
        async fetchProfile() {
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'get_user_profile',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id
                });
                this.profileContent = data.data?.content || '';
            } catch (error) {
                console.error('获取个人简介失败:', error);
            }
        },

        async saveProfile() {
            this.profileSaving = true;
            try {
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'save_user_profile',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    content: this.profileContent
                });
                this.$message.success('个人简介保存成功');
            } catch (error) {
                this.$message.error('保存失败');
            } finally {
                this.profileSaving = false;
            }
        },

        // ========== 个性标签 ==========
        async fetchTags() {
            this.tagLoading = true;
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'get_user_tags',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id
                });
                this.tagList = data.data || [];
            } catch (error) {
                console.error('获取标签失败:', error);
            } finally {
                this.tagLoading = false;
            }
        },

        openAddTagDialog() {
            this.tagForm = { tag_name: '', virtual_likes: 0, sort_order: 0 };
            this.tagDialogVisible = true;
        },

        async submitTag() {
            if (!this.tagForm.tag_name) {
                this.$message.warning('请输入标签名称');
                return;
            }
            this.tagSubmitting = true;
            try {
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'add_user_tag',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    ...this.tagForm
                });
                this.$message.success('标签添加成功');
                this.tagDialogVisible = false;
                this.fetchTags();
            } catch (error) {
                this.$message.error('添加失败');
            } finally {
                this.tagSubmitting = false;
            }
        },

        async saveTag(row) {
            try {
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'update_user_tag',
                    store_id: this.userInfo.id,
                    id: row.id,
                    virtual_likes: row.virtual_likes,
                    sort_order: row.sort_order
                });
                this.$message.success('标签更新成功');
            } catch (error) {
                this.$message.error('更新失败');
            }
        },

        async deleteTag(id) {
            try {
                await this.$confirm('确定删除该标签？', '提示', { type: 'warning' });
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'delete_user_tag',
                    store_id: this.userInfo.id,
                    id
                });
                this.$message.success('删除成功');
                this.fetchTags();
            } catch (error) {
                if (error !== 'cancel') this.$message.error('删除失败');
            }
        },

        // ========== 我的企业 ==========
        async fetchCompanies() {
            this.companyLoading = true;
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'get_user_companies',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id
                });
                this.companyList = data.data || [];
            } catch (error) {
                console.error('获取企业列表失败:', error);
            } finally {
                this.companyLoading = false;
            }
        },

        openAddCompanyDialog() {
            this.companyDialogTitle = '添加企业';
            this.isCompanyEdit = false;
            this.companyForm = { id: null, logo: '', company_name: '', industry: '', website: '', description: '', sort_order: 0 };
            this.companyDialogVisible = true;
        },

        openEditCompanyDialog(row) {
            this.companyDialogTitle = '编辑企业';
            this.isCompanyEdit = true;
            this.companyForm = { ...row };
            this.companyDialogVisible = true;
        },

        async handleCompanyLogoChange(file) {
            try {
                this.companyLogoUploading = true;
                const url = await this.uploadImage(file.raw);
                this.companyForm.logo = url;
                this.$message.success('Logo上传成功');
            } catch (error) {
                this.$message.error('Logo上传失败');
            } finally {
                this.companyLogoUploading = false;
            }
        },

        async submitCompany() {
            if (!this.companyForm.company_name) {
                this.$message.warning('请输入公司名称');
                return;
            }
            this.companySubmitting = true;
            try {
                const action = this.isCompanyEdit ? 'update_user_company' : 'add_user_company';
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action,
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    ...this.companyForm
                });
                this.$message.success(this.isCompanyEdit ? '企业更新成功' : '企业添加成功');
                this.companyDialogVisible = false;
                this.fetchCompanies();
            } catch (error) {
                this.$message.error('操作失败');
            } finally {
                this.companySubmitting = false;
            }
        },

        async deleteCompany(id) {
            try {
                await this.$confirm('确定删除该企业？', '提示', { type: 'warning' });
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'delete_user_company',
                    store_id: this.userInfo.id,
                    id
                });
                this.$message.success('删除成功');
                this.fetchCompanies();
            } catch (error) {
                if (error !== 'cancel') this.$message.error('删除失败');
            }
        },

        // ========== 视频展示 ==========
        async fetchVideos() {
            this.videoLoading = true;
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'get_user_videos',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id
                });
                this.videoList = data.data || [];
            } catch (error) {
                console.error('获取视频列表失败:', error);
            } finally {
                this.videoLoading = false;
            }
        },

        openAddVideoDialog() {
            this.videoDialogTitle = '添加视频';
            this.isVideoEdit = false;
            this.videoForm = { id: null, video_url: '', video_cover: '', video_name: '', video_desc: '', virtual_likes: 0, virtual_comments: 0, virtual_shares: 0, sort_order: 0 };
            this.videoDialogVisible = true;
        },

        openEditVideoDialog(row) {
            this.videoDialogTitle = '编辑视频';
            this.isVideoEdit = true;
            this.videoForm = { ...row };
            this.videoDialogVisible = true;
        },

        async handleVideoCoverChange(file) {
            try {
                this.videoCoverUploading = true;
                const url = await this.uploadImage(file.raw);
                this.videoForm.video_cover = url;
                this.$message.success('封面上传成功');
            } catch (error) {
                this.$message.error('封面上传失败');
            } finally {
                this.videoCoverUploading = false;
            }
        },

        async submitVideo() {
            if (!this.videoForm.video_url || !this.videoForm.video_name) {
                this.$message.warning('请填写视频URL和名称');
                return;
            }
            this.videoSubmitting = true;
            try {
                const action = this.isVideoEdit ? 'update_user_video' : 'add_user_video';
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action,
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    ...this.videoForm
                });
                this.$message.success(this.isVideoEdit ? '视频更新成功' : '视频添加成功');
                this.videoDialogVisible = false;
                this.fetchVideos();
            } catch (error) {
                this.$message.error('操作失败');
            } finally {
                this.videoSubmitting = false;
            }
        },

        async deleteVideo(id) {
            try {
                await this.$confirm('确定删除该视频？', '提示', { type: 'warning' });
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'delete_user_video',
                    store_id: this.userInfo.id,
                    id
                });
                this.$message.success('删除成功');
                this.fetchVideos();
            } catch (error) {
                if (error !== 'cancel') this.$message.error('删除失败');
            }
        },

        // ========== 图片展示 ==========
        async fetchImages() {
            this.imageLoading = true;
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'get_user_images',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id
                });
                this.imageList = data.data || [];
            } catch (error) {
                console.error('获取图片列表失败:', error);
            } finally {
                this.imageLoading = false;
            }
        },

        openAddImageDialog() {
            this.imageDialogTitle = '添加图片';
            this.isImageEdit = false;
            this.imageForm = { id: null, image_url: '', image_name: '', image_desc: '', sort_order: 0 };
            this.imageDialogVisible = true;
        },

        openEditImageDialog(row) {
            this.imageDialogTitle = '编辑图片';
            this.isImageEdit = true;
            this.imageForm = { ...row };
            this.imageDialogVisible = true;
        },

        async handleImageUploadChange(file) {
            try {
                this.imageUploadLoading = true;
                const url = await this.uploadImage(file.raw);
                this.imageForm.image_url = url;
                this.$message.success('图片上传成功');
            } catch (error) {
                this.$message.error('图片上传失败');
            } finally {
                this.imageUploadLoading = false;
            }
        },

        async submitImage() {
            if (!this.imageForm.image_url) {
                this.$message.warning('请上传图片');
                return;
            }
            this.imageSubmitting = true;
            try {
                const action = this.isImageEdit ? 'update_user_image' : 'add_user_image';
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action,
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    ...this.imageForm
                });
                this.$message.success(this.isImageEdit ? '图片更新成功' : '图片添加成功');
                this.imageDialogVisible = false;
                this.fetchImages();
            } catch (error) {
                this.$message.error('操作失败');
            } finally {
                this.imageSubmitting = false;
            }
        },

        async deleteImage(id) {
            try {
                await this.$confirm('确定删除该图片？', '提示', { type: 'warning' });
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'delete_user_image',
                    store_id: this.userInfo.id,
                    id
                });
                this.$message.success('删除成功');
                this.fetchImages();
            } catch (error) {
                if (error !== 'cancel') this.$message.error('删除失败');
            }
        },

        // ========== 音频展示 ==========
        async fetchAudios() {
            this.audioLoading = true;
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'get_user_audios',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id
                });
                this.audioList = data.data || [];
            } catch (error) {
                console.error('获取音频列表失败:', error);
            } finally {
                this.audioLoading = false;
            }
        },

        openAddAudioDialog() {
            this.audioDialogTitle = '添加音频';
            this.isAudioEdit = false;
            this.audioForm = { id: null, audio_url: '', audio_name: '', audio_desc: '', sort_order: 0 };
            this.audioDialogVisible = true;
        },

        openEditAudioDialog(row) {
            this.audioDialogTitle = '编辑音频';
            this.isAudioEdit = true;
            this.audioForm = { ...row };
            this.audioDialogVisible = true;
        },

        async submitAudio() {
            if (!this.audioForm.audio_url || !this.audioForm.audio_name) {
                this.$message.warning('请填写音频URL和名称');
                return;
            }
            this.audioSubmitting = true;
            try {
                const action = this.isAudioEdit ? 'update_user_audio' : 'add_user_audio';
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action,
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    ...this.audioForm
                });
                this.$message.success(this.isAudioEdit ? '音频更新成功' : '音频添加成功');
                this.audioDialogVisible = false;
                this.fetchAudios();
            } catch (error) {
                this.$message.error('操作失败');
            } finally {
                this.audioSubmitting = false;
            }
        },

        async deleteAudio(id) {
            try {
                await this.$confirm('确定删除该音频？', '提示', { type: 'warning' });
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'delete_user_audio',
                    store_id: this.userInfo.id,
                    id
                });
                this.$message.success('删除成功');
                this.fetchAudios();
            } catch (error) {
                if (error !== 'cancel') this.$message.error('删除失败');
            }
        },

        // ========== 产品扩展功能 ==========
        // ========== 处理联系类型切换 ==========
        handleContactTypeChange(type) {
            // 先清空
            this.contactQrcodeUrl = '';
            this.contactPhone = '';
            this.contactAppId = '';
            this.contactPath = '';
            this.contactLink = '';

            // 编辑模式下，如果已有 contact_value 则回填
            if (type && this.productForm.contact_value) {
                try {
                    // contact_value 可能是字符串，需要解析
                    let val = this.productForm.contact_value;
                    if (typeof val === 'string') {
                        val = JSON.parse(val);
                    }

                    if (type === 'qrcode') {
                        this.contactQrcodeUrl = val.qrcode || '';
                    } else if (type === 'phone') {
                        this.contactPhone = val.phone || '';
                    } else if (type === 'miniprogram') {
                        this.contactAppId = val.appid || '';
                        this.contactPath = val.path || '';
                    } else if (type === 'link') {
                        this.contactLink = val.url || '';
                    }
                } catch (e) {
                    console.log('解析联系值失败:', e);
                }
            }
        },


        // Quill 图片上传处理
        quillImageHandler(quill) {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.click();

            input.onchange = async () => {
                const file = input.files[0];
                if (!file) return;

                if (!file.type.startsWith('image/')) {
                    this.$message.error('只能上传图片文件');
                    return;
                }

                if (file.size > 5 * 1024 * 1024) {
                    this.$message.error('图片大小不能超过 5MB');
                    return;
                }

                const loading = ElLoading.service({
                    lock: true,
                    text: '图片上传中...',
                    background: 'rgba(0, 0, 0, 0.7)'
                });

                try {
                    const url = await this.uploadImage(file);
                    const range = quill.getSelection(true);
                    quill.insertEmbed(range.index, 'image', url);
                    quill.setSelection(range.index + 1);
                    loading.close();
                    this.$message.success('图片上传成功');
                } catch (error) {
                    loading.close();
                    this.$message.error(error.message || '图片上传失败');
                    console.error('图片上传失败:', error);
                }
            };
        },

        // Quill 视频上传处理
        quillVideoHandler(quill) {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'video/*');
            input.click();

            input.onchange = async () => {
                const file = input.files[0];
                if (!file) return;

                const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
                if (!allowedTypes.includes(file.type)) {
                    this.$message.error('仅支持 MP4、MOV、AVI、WebM 格式的视频');
                    return;
                }

                if (file.size > 100 * 1024 * 1024) {
                    this.$message.error('视频大小不能超过 100MB');
                    return;
                }

                const loading = ElLoading.service({
                    lock: true,
                    text: '视频上传中，请耐心等待...',
                    background: 'rgba(0, 0, 0, 0.7)'
                });

                try {
                    const url = await this.uploadVideo(file);
                    const range = quill.getSelection(true);

                    // 直接插入视频 URL，让 Quill 默认的 video blot 处理
                    quill.insertEmbed(range.index, 'video', url);
                    quill.setSelection(range.index + 1);

                    loading.close();
                    this.$message.success('视频上传成功');
                } catch (error) {
                    loading.close();
                    this.$message.error(error.message || '视频上传失败');
                    console.error('视频上传失败:', error);
                }
            };
        },
        // 上传视频到 OSS
        async uploadVideo(file) {
            try {
                const signature = await getUploadSignature('video');
                const res = await uploadToOSS(file, signature);
                return res.url;
            } catch (error) {
                console.error('视频上传失败:', error);
                throw error;
            }
        },




        // 构建联系方式值
        buildContactValue() {
            const type = this.productForm.contact_type;
            if (!type) return '';

            let value = {};
            switch (type) {
                case 'qrcode': value = { qrcode: this.contactQrcodeUrl }; break;
                case 'phone': value = { phone: this.contactPhone }; break;
                case 'miniprogram': value = { appid: this.contactAppId, path: this.contactPath }; break;
                case 'link': value = { url: this.contactLink }; break;
            }
            return JSON.stringify(value);
        },

        // 处理联系二维码上传
        async handleContactQrcodeChange(file) {
            try {
                const url = await this.uploadImage(file.raw);
                this.contactQrcodeUrl = url;
                this.productForm.contact_value = this.buildContactValue();
                this.$message.success('二维码上传成功');
            } catch (error) {
                this.$message.error('二维码上传失败');
            }
        },

        // 处理产品图集上传
        async handleProductImagesChange(file) {
            try {
                this.productImagesUploadLoading = true;
                const url = await this.uploadImage(file.raw);
                if (!this.productForm.product_images) {
                    this.productForm.product_images = [];
                }
                this.productForm.product_images.push(url);
                this.$message.success('图片上传成功');
            } catch (error) {
                this.$message.error('图片上传失败');
            } finally {
                this.productImagesUploadLoading = false;
            }
        },

        // ========== 在线客服 ==========
        async fetchCustomerServices() {
            this.csLoading = true;
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'get_customer_services',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id
                });
                this.customerServiceList = data.data || [];
            } catch (error) {
                console.error('获取客服列表失败:', error);
            } finally {
                this.csLoading = false;
            }
        },

        openAddCustomerServiceDialog() {
            this.csDialogTitle = '添加客服';
            this.isCsEdit = false;
            this.csForm = { id: null, service_type: 'wechat', service_name: '', service_account: '', qr_code: '', service_url: '', description: '', service_time: '', sort_order: 0, is_active: 1 };
            this.csDialogVisible = true;
        },

        openEditCustomerServiceDialog(row) {
            this.csDialogTitle = '编辑客服';
            this.isCsEdit = true;
            this.csForm = { ...row, is_active: parseInt(row.is_active) };
            this.csDialogVisible = true;
        },

        handleCsTypeChange() {
            this.csForm.service_account = '';
            this.csForm.qr_code = '';
            this.csForm.service_url = '';
        },

        async handleCsQrcodeChange(file) {
            try {
                this.csQrcodeUploading = true;
                const url = await this.uploadImage(file.raw);
                this.csForm.qr_code = url;
                this.$message.success('二维码上传成功');
            } catch (error) {
                this.$message.error('二维码上传失败');
            } finally {
                this.csQrcodeUploading = false;
            }
        },

        async submitCustomerService() {
            if (!this.csForm.service_type || !this.csForm.service_name) {
                this.$message.warning('请填写必填项');
                return;
            }
            this.csSubmitting = true;
            try {
                const action = this.isCsEdit ? 'update_customer_service' : 'add_customer_service';
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action,
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    ...this.csForm
                });
                this.$message.success(this.isCsEdit ? '客服更新成功' : '客服添加成功');
                this.csDialogVisible = false;
                this.fetchCustomerServices();
            } catch (error) {
                this.$message.error('操作失败');
            } finally {
                this.csSubmitting = false;
            }
        },

        async deleteCustomerService(id) {
            try {
                await this.$confirm('确定删除该客服？', '提示', { type: 'warning' });
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'delete_customer_service',
                    store_id: this.userInfo.id,
                    id
                });
                this.$message.success('删除成功');
                this.fetchCustomerServices();
            } catch (error) {
                if (error !== 'cancel') this.$message.error('删除失败');
            }
        },

        getServiceTypeTag(type) {
            const map = { wechat: 'success', phone: 'primary', qq: 'info', online: 'warning', miniprogram: 'danger' };
            return map[type] || 'info';
        },

        getServiceTypeText(type) {
            const map = { wechat: '微信客服', phone: '电话客服', qq: 'QQ客服', online: '在线客服', miniprogram: '小程序客服' };
            return map[type] || type;
        },

        // ========== 意见反馈 ==========
        async fetchFeedbackList() {
            this.feedbackLoading = true;
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'get_feedback_records',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    page: this.feedbackPagination.page,
                    page_size: this.feedbackPagination.pageSize
                });
                this.feedbackList = data.data || [];
                this.feedbackPagination.total = data.pagination?.total || 0;
            } catch (error) {
                console.error('获取反馈列表失败:', error);
            } finally {
                this.feedbackLoading = false;
            }
        },

        openReplyFeedbackDialog(row) {
            this.replyForm = {
                id: row.id,
                title: row.title,
                content: row.content,
                reply_content: row.reply_content || '',
                user_nickname: row.user_nickname,
                user_phone: row.user_phone
            };
            this.replyDialogVisible = true;
        },

        async submitReply() {
            if (!this.replyForm.reply_content) {
                this.$message.warning('请输入回复内容');
                return;
            }
            this.replySubmitting = true;
            try {
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'reply_feedback',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    id: this.replyForm.id,
                    reply_content: this.replyForm.reply_content
                });
                this.$message.success('回复成功');
                this.replyDialogVisible = false;
                this.fetchFeedbackList();
            } catch (error) {
                this.$message.error('回复失败');
            } finally {
                this.replySubmitting = false;
            }
        },

        getFeedbackTypeTag(type) {
            const map = { bug: 'danger', suggestion: 'primary', complaint: 'warning', other: 'info' };
            return map[type] || 'info';
        },

        getFeedbackTypeText(type) {
            const map = { bug: '程序错误', suggestion: '功能建议', complaint: '投诉', other: '其他' };
            return map[type] || type;
        },

        getFeedbackStatusTag(status) {
            const map = { 0: 'info', 1: 'warning', 2: 'success', 3: 'danger' };
            return map[parseInt(status)] || 'info';
        },

        getFeedbackStatusText(status) {
            const map = { 0: '待处理', 1: '处理中', 2: '已处理', 3: '已关闭' };
            return map[parseInt(status)] || '未知';
        },

        // ========== 关于我们 ==========
        async fetchAboutUs() {
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'get_about_us',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id
                });
                if (data.data) {
                    this.aboutUsForm = { ...data.data, social_links: data.data.social_links || [], images: data.data.images || [] };
                }
            } catch (error) {
                console.error('获取关于我们失败:', error);
            }
        },

        addSocialLink() {
            if (!this.aboutUsForm.social_links) this.aboutUsForm.social_links = [];
            this.aboutUsForm.social_links.push({ icon: '', name: '', url: '' });
        },

        removeSocialLink(index) {
            this.aboutUsForm.social_links.splice(index, 1);
        },

        async uploadSocialIcon(file, index) {
            try {
                const url = await this.uploadImage(file.raw);
                this.aboutUsForm.social_links[index].icon = url;
                this.$message.success('图标上传成功');
            } catch (error) {
                this.$message.error('图标上传失败');
            }
        },

        async handleAboutUsLogoChange(file) {
            try {
                this.aboutUsLogoUploading = true;
                const url = await this.uploadImage(file.raw);
                this.aboutUsForm.logo = url;
                this.$message.success('Logo上传成功');
            } catch (error) {
                this.$message.error('Logo上传失败');
            } finally {
                this.aboutUsLogoUploading = false;
            }
        },

        async handleAboutUsImageChange(file) {
            try {
                this.aboutUsImageUploading = true;
                const url = await this.uploadImage(file.raw);
                if (!this.aboutUsForm.images) this.aboutUsForm.images = [];
                this.aboutUsForm.images.push(url);
                this.$message.success('图片上传成功');
            } catch (error) {
                this.$message.error('图片上传失败');
            } finally {
                this.aboutUsImageUploading = false;
            }
        },

        async saveAboutUs() {
            this.aboutUsSaving = true;
            try {
                await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'save_about_us',
                    store_id: this.userInfo.id,
                    card_id: this.currentCard.id,
                    ...this.aboutUsForm,
                    social_links: JSON.stringify(this.aboutUsForm.social_links),
                    images: JSON.stringify(this.aboutUsForm.images)
                });
                this.$message.success('关于我们保存成功');
            } catch (error) {
                this.$message.error('保存失败');
            } finally {
                this.aboutUsSaving = false;
            }
        },

        // ========== 提交产品表单（修改版）==========
        async submitProductForm() {
            this.$refs.productForm.validate(async (valid) => {
                if (valid) {
                    this.productSubmitting = true;
                    try {
                        // 构建联系方式值
                        this.productForm.contact_value = this.buildContactValue();

                        const payload = {
                            action: this.isProductEditMode ? 'update_product' : 'add_product',
                            store_id: this.userInfo.id,
                            card_id: this.currentCard.id,
                            product_title: this.productForm.product_title,
                            product_desc: this.productForm.product_desc || '',
                            product_detail: this.productForm.product_detail || '',
                            product_image: this.productForm.product_image || '',
                            product_images: JSON.stringify(this.productForm.product_images || []),
                            product_price: this.productForm.product_price || '',
                            product_rating: parseFloat(this.productForm.product_rating) || 0,
                            product_tag: this.productForm.product_tag || '',
                            contact_type: this.productForm.contact_type || '',
                            contact_value: this.productForm.contact_value || '',
                            product_url: this.productForm.product_url || '',
                            sort_order: this.productForm.sort_order || 0,
                            is_active: this.productForm.is_active ? 1 : 0
                        };

                        // 编辑模式需要传id
                        if (this.isProductEditMode) {
                            payload.id = this.productForm.id;
                        }

                        const { data } = await axios.post(
                            `${config.BASE_URL}/shop/api/business_card_management.php`,
                            payload
                        );

                        if (data.error) {
                            this.$message.error(data.error);
                        } else {
                            this.$message.success(this.isProductEditMode ? '产品更新成功' : '产品添加成功');
                            this.productFormDialogVisible = false;
                            this.fetchProductList(this.currentCard.id);
                        }
                    } catch (error) {
                        let errorMsg = '操作失败';
                        if (error.response?.data?.error) errorMsg = error.response.data.error;
                        this.$message.error(errorMsg);
                        console.error('产品表单提交错误:', error);
                    } finally {
                        this.productSubmitting = false;
                    }
                }
            });
        },

        // 删除名片
        async handleDelete(id) {
            this.$confirm('确定要删除该名片吗？', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/business_card_management.php`,
                        {
                            action: 'delete',
                            id: id,
                            store_id: this.userInfo.id
                        }
                    )

                    if (response.data.error) {
                        this.$message.error(response.data.error)
                    } else {
                        this.$message.success('删除成功')
                        this.fetchCardList()
                    }
                } catch (error) {
                    let errorMsg = '删除失败'
                    if (error.response && error.response.data && error.response.data.error) {
                        errorMsg = error.response.data.error
                    }
                    this.$message.error(errorMsg)
                    console.error('删除错误:', error)
                }
            }).catch(() => { })
        },

        // 删除产品
        async handleDeleteProduct(id) {
            this.$confirm('确定要删除该产品吗？', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const response = await axios.post(
                        `${config.BASE_URL}/shop/api/business_card_management.php`,
                        {
                            action: 'delete_product',
                            id: id,
                            store_id: this.userInfo.id
                        }
                    )

                    if (response.data.error) {
                        this.$message.error(response.data.error)
                    } else {
                        this.$message.success('产品删除成功')
                        this.fetchProductList(this.currentCard.id)
                    }
                } catch (error) {
                    let errorMsg = '删除失败'
                    if (error.response && error.response.data && error.response.data.error) {
                        errorMsg = error.response.data.error
                    }
                    this.$message.error(errorMsg)
                    console.error('删除产品错误:', error)
                }
            }).catch(() => { })
        },

        // 设置为主名片
        async setAsPrimary(row) {
            if (row.is_primary) {
                return
            }

            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'set_primary',
                        id: row.id,
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.error) {
                    this.$message.error(response.data.error)
                } else {
                    this.$message.success('设置主名片成功')
                    this.fetchCardList()
                }
            } catch (error) {
                let errorMsg = '设置失败'
                if (error.response && error.response.data && error.response.data.error) {
                    errorMsg = error.response.data.error
                }
                this.$message.error(errorMsg)
                console.error('设置主名片错误:', error)
            }
        },

        // 生成TX广告链接
        getOffiaccountUrl(cardId) {
            const baseDomain = window.location.origin
            return `${baseDomain}/offiaccount/?store_id=${this.userInfo.id}&card_id=${cardId}`
        },

        // 生成H5链接
        getH5Url(cardId) {
            const baseDomain = window.location.origin
            return `${baseDomain}/h5/?store_id=${this.userInfo.id}&card_id=${cardId}`
        },

        // 生成小程序链接
        getMiniUrl(cardId) {
            return `pages/home/home?store_id=${this.userInfo.id}&card_id=${cardId}`
        },

        // 复制链接
        copyUrl(url) {
            navigator.clipboard.writeText(url).then(() => {
                this.$message.success('链接已复制到剪贴板')
            }).catch(() => {
                const textArea = document.createElement('textarea')
                textArea.value = url
                document.body.appendChild(textArea)
                textArea.select()
                document.execCommand('copy')
                document.body.removeChild(textArea)
                this.$message.success('链接已复制到剪贴板')
            })
        }
    }
}
</script>

<style scoped lang="scss">
.page-container {
    padding: 20px;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.stats-info {
    display: flex;
    gap: 20px;
    background: #f0f7ff;
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
}

.stat-item {
    display: flex;
    align-items: center;

    .label {
        color: #606266;
        font-weight: 500;
    }

    .value {
        font-weight: 600;
        color: #409eff;

        &.primary {
            color: #67C23A;
        }
    }
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
    padding: 32px 40px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
    font-size: 24px;
    color: #1f2937;
    margin-bottom: 32px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.5px;
}

.search-bar {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

.product-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #ebeef5;

    h3 {
        margin: 0;
        color: #1f2937;
    }
}

.el-table {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;

    :deep(.el-table__header) th {
        background-color: #f5f7fa;
        color: #606266;
        font-weight: 600;
    }

    :deep(.el-table__body) tr:hover>td {
        background-color: #f0f7ff !important;
    }
}

.primary-icon {
    transition: all 0.3s ease;

    &:hover {
        transform: scale(1.2);
    }
}

.user-info {
    .user-nickname {
        font-weight: 500;
        margin-bottom: 4px;
    }

    .user-phone {
        color: #909399;
        font-size: 12px;
    }
}

.gzh-info {
    .gzh-name {
        font-weight: 500;
        margin-bottom: 4px;
    }

    .gzh-appid {
        color: #909399;
        font-size: 12px;
    }
}

.user-option,
.gzh-option {
    display: flex;
    align-items: center;
}

.card-url-section {
    display: flex;
    align-items: center;
    gap: 8px;
}

.url-list {
    .url-item {
        margin-bottom: 12px;

        &:last-child {
            margin-bottom: 0;
        }

        .url-label {
            font-weight: 500;
            color: #606266;
            font-size: 14px;
        }

        .url-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-top: 4px;
            padding: 8px;
            background: #f5f7fa;
            border-radius: 4px;

            .url-text {
                flex: 1;
                font-size: 12px;
                color: #409eff;
                word-break: break-all;
                margin-right: 8px;
            }
        }
    }
}

.empty-value {
    color: #909399;
    font-style: italic;
}

.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 6px;
}

.image-slot {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: #f5f7fa;
    color: #909399;
    font-size: 12px;
}

:deep(.el-dialog) {
    border-radius: 12px;

    .el-dialog__header {
        padding: 20px 24px;
        border-bottom: 1px solid #ebeef5;
        margin-right: 0;

        .el-dialog__title {
            font-weight: 600;
            color: #1f2937;
        }
    }

    .el-dialog__body {
        padding: 24px;
    }

    .el-dialog__footer {
        padding: 16px 24px;
        border-top: 1px solid #ebeef5;
    }
}

.el-button {
    display: inline-flex;
    align-items: center;

    .el-icon {
        margin-right: 5px;
    }
}

// 头像和图片上传样式
.avatar-upload,
.image-upload {
    display: flex;
    align-items: flex-start;
    gap: 20px;
}

.avatar-preview,
.image-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.avatar-image {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    border: 2px solid #ebeef5;
    overflow: hidden;
}

.product-image {
    width: 100px;
    height: 100px;
    border-radius: 8px;
    border: 2px solid #ebeef5;
    overflow: hidden;
}

.avatar-actions,
.image-actions {
    display: flex;
    justify-content: center;
}

.avatar-uploader,
.image-uploader {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.el-upload__tip {
    font-size: 12px;
    color: #909399;
    margin-top: 5px;
}

// 广告对话框样式
.ad-audience-dialog {
    .platform-tabs {
        margin-bottom: 20px;

        :deep(.el-tabs__header) {
            margin: 0;
        }

        :deep(.el-tabs__content) {
            padding: 0;
        }
    }

    .coming-soon {
        padding: 40px 20px;
        text-align: center;
        background: #f8f9fa;
        border-radius: 8px;
        min-height: 300px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .bound-audiences-section {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #ebeef5;

        .section-title {
            font-size: 16px;
            color: #1f2937;
            margin-bottom: 15px;
            font-weight: 600;
        }
    }
}

// 响应式调整
@media (max-width: 768px) {

    .avatar-upload,
    .image-upload {
        flex-direction: column;
        align-items: center;
    }
}

/* 在小程序路径样式部分 */
.mini-qrcode-actions {
    display: flex;
    gap: 4px;
}

.qrcode-display {
    margin-top: 12px;
    padding: 12px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #ebeef5;
}

.qrcode-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    color: #606266;
    font-weight: 500;
}

.qrcode-content {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.qrcode-image {
    width: 150px;
    height: 150px;
    border-radius: 6px;
    border: 1px solid #ddd;
    margin-bottom: 8px;
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
        transform: scale(1.05);
    }
}

.qrcode-actions {
    display: flex;
    gap: 12px;
    margin-top: 8px;
}

.qrcode-tip {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #909399;
    font-size: 12px;
    margin-top: 8px;
    padding: 8px;
    background: #f5f5f5;
    border-radius: 4px;
}

/* 二维码预览样式 */
.qrcode-preview-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
}

.preview-qrcode-image {
    width: 250px;
    height: 250px;
    border-radius: 8px;
    border: 2px solid #ebeef5;
    margin-bottom: 20px;
}

.preview-actions {
    display: flex;
    gap: 15px;
    margin-bottom: 15px;
}

.preview-tip {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #409eff;
    font-size: 14px;
    padding: 10px;
    background: #f0f7ff;
    border-radius: 6px;
    border: 1px solid #d9ecff;
}

// 富文本编辑器
.quill-editor-wrapper {
    width: 100%;

    :deep(.ql-container) {
        min-height: 200px;
    }

    :deep(.ql-editor) {
        min-height: 200px;
    }
}

// 自定义管理对话框
.custom-tab-content {
    padding: 10px 0;
}

.homepage-subtabs {
    :deep(.el-tabs__header) {
        margin-bottom: 0;
    }
}


// 反馈图片
.feedback-images {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

// 上传区域
.upload-section {
    display: flex;
    align-items: flex-start;
    gap: 12px;

    .logo-preview {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
    }
}

// 产品图集上传区域
.product-images-upload-area {
    margin-bottom: 12px;
}

// 产品图集画廊 - 按钮在上，图片在下
.product-images-gallery {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    .gallery-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 6px;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        background: #fafafa;
    }
}

// 视频预览样式
.video-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 8px;
    background: #f5f7fa;
    border-radius: 8px;

    video {
        max-width: 100%;
        border-radius: 4px;
    }
}

// 音频预览样式
.audio-preview {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 8px;
    background: #f5f7fa;
    border-radius: 8px;

    audio {
        width: 100%;
    }
}

// 社交媒体链接滚动区域
.social-links-scroll {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px;
    background: #fafafa;

    .social-link-item {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        padding: 8px;
        background: #ffffff;
        border-radius: 6px;
        border: 1px solid #e4e7ed;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .empty-links {
        text-align: center;
        padding: 20px;
        color: #909399;
        font-size: 13px;
    }
}

// 相关图片区域
.about-us-images {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 0; // 移除上边距，因为按钮已经在上方

    .about-us-image-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 6px;
        border: 1px solid #ebeef5;
        border-radius: 6px;
        background: #fafafa;
    }

    .empty-images {
        text-align: center;
        padding: 20px;
        color: #909399;
        font-size: 13px;
        width: 100%;
        background: #fafafa;
        border-radius: 6px;
        border: 1px dashed #dcdfe6;
    }
}
</style>